import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useId,
} from 'react';
import type {
  MasterCatalogItem,
  Template,
} from '@ohif/core/src/services/TemplateService/TemplateService';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import {
  Icons,
  useModal,
  Onboarding,
  cn,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@ohif/ui-next';

import { parseDocument, SUPPORTED_FILE_TYPES } from '../../custom/utils/documentParser';

import PageLayout from '../../custom/components/layouts/PageLayout';
import { Unauthorized } from '../../custom/components';
import { authService } from '../../custom/auth/authService';
import { useAuthUser } from '../../custom/hooks/useMenuItems';
import { canAccessPage } from '../../custom/utils/permissions';
import { hasRole, ROLES } from '../../custom/utils/roleUtils';

import {
  BodyPartDropdown,
  VirtualizedDropdown,
  VirtualizedDropdownOption,
  ToggleSwitch,
  Pagination,
} from '../../custom/components/common';
import './Templates.css';
import '../../custom/pages/superadmin/UserManagement.css';
import { Copy, Library, Plus, RefreshCw, Search, Trash2, Upload, X } from 'lucide-react';
import {
  CREATE_TEMPLATE_MODAL_DIALOG_CLASS,
  IMPORT_LIBRARY_MODAL_DIALOG_CLASS,
  UPLOAD_TEMPLATE_MODAL_DIALOG_CLASS,
  BULK_UPLOAD_REVIEW_MODAL_DIALOG_CLASS,
  VIEW_UPDATE_TEMPLATE_MODAL_DIALOG_CLASS,
  ICON_STROKE_WIDTH,
  TEMPLATE_CONTENT_VALIDATION_DELAY_MS,
  TEMPLATE_IMPORT_SUPPORTED_EXTENSIONS,
  TEMPLATE_MAX_CONTENT_WORDS,
  TEMPLATE_MODALITY_FILTER_OPTIONS,
  TEMPLATE_MODALITY_OPTIONS,
  TEMPLATE_QUILL_FORMATS,
  TEMPLATE_QUILL_MODULES,
  TEMPLATE_STATUS_FILTER_OPTIONS,
  TEMPLATE_TYPE_FILTER_OPTIONS,
  TEMPLATES_API_FETCH_LIMIT,
  TEMPLATES_ITEMS_PER_PAGE,
  TEMPLATE_BULK_UPLOAD_AUTO_CREATE_THRESHOLD,
  TEMPLATE_FOLDER_IMPORT_MAX_DEPTH,
  TEMPLATE_FOLDER_IMPORT_MAX_FILES,
  TEMPLATE_FOLDER_IMPORT_MAX_TOTAL_BYTES,
  TEMPLATE_UPLOAD_DUPLICATE_CHECK_MAX_PAGES,
} from '../../custom/constants';
import { API_ENDPOINTS } from '../../custom/config/api.config';
import { fetchAndStoreModalities, getModalities } from '../../custom/utils/modalityUtils';
import {
  batchUploadShouldRefreshWithoutClose,
  ensureMinUploadTemplateName,
  findWithinBatchDuplicateFlags,
  truncateFilesByImportCaps,
  uniqueBulkTemplateNames,
  uploadDuplicateKey,
  withResolvedBulkFileNames,
} from './templateUploadHelpers';

/** Map permission icon names to AppSidebar Lucide icon names */
interface ServicesManager {
  services: {
    customizationService: Record<string, unknown>;
    templateService: Record<string, unknown>;
    uiNotificationService: Record<string, unknown>;
  };
}

interface TemplatesProps {
  servicesManager: ServicesManager;
}

/** Figma “Last modified at”: localized date + time */
function formatTemplateDateTime(iso: string | undefined): string {
  if (!iso) {
    return '—';
  }
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return '—';
    }
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

function modalityBadgeLabel(modalityCode: string): string {
  const key = (modalityCode || '').trim().toUpperCase();
  const map: Record<string, string> = {
    MR: 'MRI',
    CT: 'CT',
    XR: 'X-Ray',
    US: 'USG',
  };
  return map[key] || modalityCode || '—';
}

/** Read-only compare: Quill HTML or empty em dash (Figma). */
function formatQuillHtmlForCompare(s: string | undefined): string {
  const t = (s || '').trim();
  if (!t || t === '<p><br></p>' || t === '<p></p>') {
    return '<p class="templates-view-update-empty">—</p>';
  }
  return t;
}

type TemplateServiceCompareApi = {
  fetchTemplateWithMaster: (id: string) => Promise<{
    current: Template;
    master: Template | null;
  }>;
  postTemplatesMasterAction: (
    templateIds: number[],
    action: 'UPDATE' | 'KEEP_CURRENT'
  ) => Promise<{
    message?: string;
    action: 'UPDATE' | 'KEEP_CURRENT';
    results: Array<{ id: number; status: string; action?: string; message?: string }>;
  }>;
};

/** List columns: only RIS `view` / `diagnosis` (mapped to viewLabel / diagnosisLabel). No impression or title heuristics. */
function templateViewDisplay(template: Template): string {
  return template.viewLabel?.trim() || '—';
}

function templateDiagnosisDisplay(template: Template): string {
  return template.diagnosisLabel?.trim() || '—';
}

function templateModifiedByDisplay(template: Template): string {
  return template.updatedByName?.trim() || '—';
}

function templateListTypeDisplay(template: Template): 'Custom' | 'System' {
  return template.listType === 'System' ? 'System' : 'Custom';
}

function templateIsActive(t: Template): boolean {
  return !t.is_deleted && t.status !== 'Inactive';
}

/** Header + template name field: show this many characters, then an ellipsis (full name via title / focus). */
const TEMPLATE_NAME_DISPLAY_MAX = 20;

/** Master-template create (ris-super-admin): fallback before any modality is chosen. */
const MASTER_TEMPLATE_NEW_DISPLAY_NAME = 'Untitled template';
/** Full template title (modality + body + view + diagnosis + template_description) max length (ris-radiologist). */
const TEMPLATE_AUTO_NAME_MAX_LEN = 50;

/** Figma-style separator between segments (e.g. `MRI — Spine — PA — Cancer`). */
const TEMPLATE_AUTO_NAME_SEPARATOR = ' \u2014 ';

/** Bulk status: selection must be only inactive (for ACTIVATE) or only active (for DEACTIVATE). */
const BULK_TEMPLATE_STATUS_MIXED_SELECTION_MESSAGE =
  'Please select either active or inactive templates to proceed further.';

/** Figma copy for Findings / Impressions empty state (inside editor). */
const TEMPLATE_QUILL_NARRATIVE_PLACEHOLDER =
  'Enter narrative text. Rich text editing will replace this field.';

/** Figma helper line below both editors (still shows live combined count). */
const TEMPLATE_COMBINED_HINT_BRACKETS =
  'Use [brackets] for placeholder fields that need completion at report time.';

/** Map modality code to the short label used in generated template names (matches list/API style). */
function modalityCodeToTemplateNameSegment(code: string): string {
  const c = (code || '').trim().toUpperCase();
  if (c === 'MR') {
    return 'MRI';
  }
  if (c === 'US') {
    return 'USG';
  }
  if (c === 'XR') {
    return 'X-Ray';
  }
  if (c === 'CT') {
    return 'CT';
  }
  return c;
}

/** Live preview for master create: modality → body part → view → diagnosis (non-empty parts only). */
function buildMasterTemplateAutoDisplayName(
  modalityCode: string,
  bodyPartLabel: string,
  view: string,
  diagnosis: string
): string {
  const modSeg = modalityCode ? modalityCodeToTemplateNameSegment(modalityCode) : '';
  const parts = [
    modSeg,
    (bodyPartLabel || '').trim(),
    (view || '').trim(),
    (diagnosis || '').trim(),
  ].filter(s => s.length > 0);
  return parts.join(TEMPLATE_AUTO_NAME_SEPARATOR);
}

/** ris-radiologist new template: live title = auto prefix (or Untitled) + optional template_description suffix. */
function combineRadiologistTemplateTitle(prefix: string, suffix: string, fallback: string): string {
  const p = (prefix || '').trim();
  const s = (suffix || '').replace(/\s/g, '').trim();
  const base = p || fallback;
  if (s) {
    return `${base}${TEMPLATE_AUTO_NAME_SEPARATOR}${s}`;
  }
  return base;
}

/** Same “base” as `combineRadiologistTemplateTitle` before appending template_description (for 50-char total limit). */
function getRadiologistTitleBaseForLimit(params: {
  isRadiologistCatalogNewCreate: boolean;
  isRadiologistTenantEdit: boolean;
  tenantSystemTemplateReadOnly: boolean;
  template: Template | undefined | null;
  radiologistCatalogPrefixLine: string;
}): string {
  const {
    isRadiologistCatalogNewCreate,
    isRadiologistTenantEdit,
    tenantSystemTemplateReadOnly,
    template,
    radiologistCatalogPrefixLine,
  } = params;
  if (isRadiologistCatalogNewCreate) {
    const p = (radiologistCatalogPrefixLine || '').trim();
    return p || MASTER_TEMPLATE_NEW_DISPLAY_NAME;
  }
  if (isRadiologistTenantEdit && !tenantSystemTemplateReadOnly && template) {
    const prefixForCombine =
      (radiologistCatalogPrefixLine || '').trim() ||
      (template.templateNameWithoutDescription
        ? formatTemplateNameForListDisplay(String(template.templateNameWithoutDescription))
        : '');
    const fallback = formatTemplateNameForListDisplay(template.name);
    return (prefixForCombine || '').trim() || fallback;
  }
  return '';
}

function radiologistFullTitleLength(base: string, suffixWithoutSpaces: string): number {
  const s = (suffixWithoutSpaces || '').replace(/\s/g, '');
  if (!s) {
    return base.length;
  }
  return base.length + TEMPLATE_AUTO_NAME_SEPARATOR.length + s.length;
}

function computeRadiologistMaxSuffixChars(base: string): number {
  return Math.max(0, TEMPLATE_AUTO_NAME_MAX_LEN - base.length - TEMPLATE_AUTO_NAME_SEPARATOR.length);
}

/**
 * Enforce catalog title budget on View/Diagnosis only (modality/body part stay as selected).
 * Trims diagnosis from the end, then view, until `buildMasterTemplateAutoDisplayName` ≤ maxLen.
 */
function clampViewAndDiagnosisToCatalogPrefixMax(
  modalityCode: string,
  bodyPartLabel: string,
  viewText: string,
  diagnosisText: string,
  maxLen: number
): { view: string; diagnosis: string } {
  let view = viewText || '';
  let diagnosis = diagnosisText || '';

  const measure = () =>
    buildMasterTemplateAutoDisplayName(modalityCode, bodyPartLabel, view, diagnosis).trim().length;

  while (measure() > maxLen) {
    if (diagnosis.length > 0) {
      diagnosis = diagnosis.slice(0, -1);
    } else if (view.length > 0) {
      view = view.slice(0, -1);
    } else {
      break;
    }
  }
  return { view, diagnosis };
}

function truncateTemplateNameForDisplay(
  name: string,
  maxLen: number = TEMPLATE_NAME_DISPLAY_MAX
): string {
  if (!name || name.length <= maxLen) {
    return name;
  }
  return `${name.slice(0, maxLen)}…`;
}

/** API `template_name` uses `--` between segments; show em dashes like the template header (TEMPLATE_AUTO_NAME_SEPARATOR). */
function formatTemplateNameForListDisplay(name: string): string {
  if (!name) {
    return '';
  }
  return name.replace(/--/g, TEMPLATE_AUTO_NAME_SEPARATOR).trim();
}

/** Matches the templates table name column: pretty separators + `(copyN)` when API sends `copy_version`. */
function templateRowDisplayName(template: Template): string {
  const pretty = formatTemplateNameForListDisplay(template.name);
  const cv = template.copyVersion;
  if (cv != null && String(cv).trim() !== '') {
    const n = String(cv).trim();
    if (!/\(copy\d+\)/i.test(pretty)) {
      return `${pretty} (copy${n})`;
    }
  }
  return pretty;
}

/** Initial header body-part badge text from API template (category string or nested anatomy name). */
function getTemplateBodyPartHeaderLabel(t: Template | undefined | null): string {
  if (!t) {
    return '';
  }
  const cat = ((t as unknown as { anatomy_category?: string }).anatomy_category || '').trim();
  if (cat) {
    return cat;
  }
  return (
    (t as unknown as { AnatomyPart?: { name?: string } | null }).AnatomyPart?.name || ''
  ).trim();
}

/** Word count from HTML (Quill) — mirrors CreateTemplateModal.getWordCount */
function getWordCountFromHtml(content: string): number {
  const plain = content
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!plain) {
    return 0;
  }
  return plain.split(/\s+/).filter(Boolean).length;
}

function Templates({ servicesManager }: TemplatesProps) {
  const { show, hide } = useModal();
  const { templateService, uiNotificationService } = servicesManager.services;

  // Get current user to check permissions
  const user = useAuthUser();
  const isSuperAdmin = hasRole(user, ROLES.SUPER_ADMIN);
  /** Tenant radiologist (not master-catalog super-admin): “Import Templates” from platform library */
  const isRisRadiologistTenant = hasRole(user, ROLES.RIS_RADIOLOGIST) && !isSuperAdmin;

  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedModality, setSelectedModality] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBodyPartId, setSelectedBodyPartId] = useState<number | null>(null);
  const [templatesMessage, setTemplatesMessage] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'active' | 'inactive'>('all');
  /** Tenant (ris-radiologist): API `type=SYSTEM` | `CUSTOM` — Figma “All types” */
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<'all' | 'system' | 'custom'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  /** Server total count for ris-super-admin master-templates (server-side pagination). */
  const [masterListTotalItems, setMasterListTotalItems] = useState(0);

  /** Tenant: `total_update_available` from GET /templates (catalogue update notifier). */
  const [totalCatalogUpdatesAvailable, setTotalCatalogUpdatesAvailable] = useState(0);
  /** Tenant: `update_available_system_template_ids` — bulk Accept / Decline targets. */
  const [updateAvailableSystemTemplateIds, setUpdateAvailableSystemTemplateIds] = useState<
    number[]
  >([]);
  const [catalogBannerBulkBusy, setCatalogBannerBulkBusy] = useState(false);

  /** Tenant radiologist: bulk row selection (foundation for bulk actions). */
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<string>>(new Set());
  const [tenantBulkStatusBusy, setTenantBulkStatusBusy] = useState(false);
  /** ris-radiologist table Duplicate icon: POST create without opening modal */
  const [duplicateTemplateBusyId, setDuplicateTemplateBusyId] = useState<string | null>(null);

  /** Right-hand panel: editing an existing row only. New template uses centered modal (Figma). */
  type TemplatePanelPayload = null | {
    template: Template;
    startInViewMode: boolean;
  };

  const [templatePanel, setTemplatePanel] = useState<TemplatePanelPayload>(null);
  const [templatePanelNonce, setTemplatePanelNonce] = useState(0);

  const closeTemplatePanel = useCallback(() => {
    setTemplatePanel(null);
  }, []);

  useEffect(() => {
    if (!isRisRadiologistTenant) {
      return;
    }
    setSelectedTemplateIds(new Set());
  }, [
    isRisRadiologistTenant,
    searchTerm,
    selectedModality,
    selectedBodyPartId,
    selectedStatus,
    selectedTypeFilter,
  ]);

  const toggleTemplateRowSelected = useCallback((id: string, checked: boolean) => {
    setSelectedTemplateIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;

  const loadTemplates = useCallback(async () => {
    const effectiveUser = user ?? authService.getUser();
    if (!effectiveUser || !canAccessPage(effectiveUser, '/templates')) {
      return;
    }
    const useMasterTemplatesList = hasRole(effectiveUser, ROLES.SUPER_ADMIN);

    if (!templateService) {
      return;
    }

    try {
      if (useMasterTemplatesList) {
        let modalities = getModalities();
        if (!modalities?.length) {
          try {
            await fetchAndStoreModalities();
          } catch (e) {
            console.error('Error loading modalities for master templates:', e);
          }
          modalities = getModalities();
        }
        const modalityId =
          selectedModality && modalities?.length
            ? modalities.find(m => m.name === selectedModality)?.id
            : undefined;

        const statusParam: 'active' | 'inactive' | undefined =
          selectedStatus === 'all' ? undefined : selectedStatus;

        const result = await templateService.fetchTemplatesFromApi({
          page: currentPage,
          limit: TEMPLATES_ITEMS_PER_PAGE,
          modality_id: modalityId,
          anatomy_id: selectedBodyPartId ?? undefined,
          search: searchTermRef.current.trim() || undefined,
          status: statusParam,
          templatesListBaseUrl: API_ENDPOINTS.MASTER_TEMPLATES.GET,
        });
        setTemplates(result.templates || []);
        setTemplatesMessage(result.message || '');
        setMasterListTotalItems(result.total ?? 0);
        setTotalCatalogUpdatesAvailable(0);
        setUpdateAvailableSystemTemplateIds([]);
      } else {
        const result = await templateService.fetchTemplatesFromApi({
          page: 1,
          limit: TEMPLATES_API_FETCH_LIMIT,
          modality: selectedModality || undefined,
          anatomy_id: selectedBodyPartId ?? undefined,
          search: searchTermRef.current.trim() || undefined,
          ...(selectedStatus !== 'all'
            ? { status: selectedStatus }
            : {}),
          ...(selectedTypeFilter !== 'all'
            ? {
                templatesListType:
                  selectedTypeFilter === 'system' ? ('SYSTEM' as const) : ('CUSTOM' as const),
              }
            : {}),
        });
        setTemplates(result.templates || []);
        setTemplatesMessage(result.message || '');
        setMasterListTotalItems(0);
        const mapped = result.templates || [];
        const apiIds = result.updateAvailableSystemTemplateIds;
        const idsFromPayload =
          Array.isArray(apiIds) && apiIds.length > 0
            ? apiIds
                .map(id => (typeof id === 'number' ? id : Number(id)))
                .filter(id => Number.isFinite(id) && id > 0)
            : mapped
                .filter(t => t.updateAvailable)
                .map(t => Number(t.id))
                .filter(id => Number.isFinite(id) && id > 0);
        setUpdateAvailableSystemTemplateIds(idsFromPayload);

        const fallbackCount = idsFromPayload.length;
        const fromApi = result.totalUpdateAvailable;
        setTotalCatalogUpdatesAvailable(
          typeof fromApi === 'number' && fromApi >= 0 ? fromApi : fallbackCount
        );
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'Failed to load templates.';
      setTemplatesMessage(message);
      setMasterListTotalItems(0);
      setTotalCatalogUpdatesAvailable(0);
      setUpdateAvailableSystemTemplateIds([]);
      setTemplates([]);
    }
  }, [
    user,
    templateService,
    selectedModality,
    selectedBodyPartId,
    selectedStatus,
    selectedTypeFilter,
    currentPage,
  ]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const loadTemplatesRef = useRef(loadTemplates);
  loadTemplatesRef.current = loadTemplates;

  const openCreateTemplateModal = useCallback(
    (payload?: {
      importedContent?: string;
      suggestedName?: string;
      fromUploadFlow?: boolean;
      batchDocuments?: Array<{ importedContent: string; suggestedName: string }>;
      duplicateFrom?: Template;
    }) => {
      show({
        content: CreateTemplateModal,
        title: '',
        showCloseButton: payload?.fromUploadFlow ? false : undefined,
        contentProps: {
          servicesManager,
          hide,
          onSave: () => {
            hide();
            loadTemplatesRef.current();
          },
          onRefreshList: () => {
            loadTemplatesRef.current();
          },
          importedContent: payload?.importedContent,
          suggestedName: payload?.suggestedName,
          fromUploadFlow: payload?.fromUploadFlow ?? false,
          batchDocuments: payload?.batchDocuments,
          duplicateFrom: payload?.duplicateFrom,
          embedded: false,
        },
        containerClassName: CREATE_TEMPLATE_MODAL_DIALOG_CLASS,
      });
    },
    [hide, servicesManager, show]
  );

  const openViewUpdateModal = useCallback(
    (id: string) => {
      show({
        content: ViewTemplateUpdateModal,
        title: '',
        showCloseButton: false,
        contentProps: {
          servicesManager,
          templateId: id,
          hide,
          onApplied: () => {
            loadTemplatesRef.current();
          },
        },
        containerClassName: VIEW_UPDATE_TEMPLATE_MODAL_DIALOG_CLASS,
      });
    },
    [hide, servicesManager, show]
  );

  const handleCatalogUpdateBannerBulk = useCallback(
    async (action: 'UPDATE' | 'KEEP_CURRENT') => {
      if (!templateService || catalogBannerBulkBusy) {
        return;
      }
      const ids = [...updateAvailableSystemTemplateIds];
      if (ids.length === 0) {
        uiNotificationService.show({
          title: 'Nothing to apply',
          message:
            'No template ids with pending catalogue updates. Refresh the page and try again.',
          type: 'info',
          duration: 4500,
        });
        return;
      }
      setCatalogBannerBulkBusy(true);
      try {
        const svc = templateService as TemplateServiceCompareApi;
        const { message, results } = await svc.postTemplatesMasterAction(ids, action);
        const failed = results.filter(r => r.status !== 'ok');
        if (failed.length === 0) {
          uiNotificationService.show({
            title: 'Success',
            message:
              message ||
              (action === 'UPDATE'
                ? 'Templates updated to the latest catalogue version.'
                : 'Kept your current template versions.'),
            type: 'success',
            duration: 3500,
          });
        } else {
          const detail = failed
            .map(f => (f.message ? `ID ${f.id}: ${f.message}` : `ID ${f.id}`))
            .join(' ');
          uiNotificationService.show({
            title: failed.length === results.length ? 'Error' : 'Partially completed',
            message: detail || message || 'Some templates could not be updated.',
            type: failed.length === results.length ? 'error' : 'warning',
            duration: 7000,
          });
        }
        loadTemplatesRef.current();
      } catch (e) {
        uiNotificationService.show({
          title: 'Error',
          message: e instanceof Error ? e.message : 'Request failed.',
          type: 'error',
          duration: 5000,
        });
      } finally {
        setCatalogBannerBulkBusy(false);
      }
    },
    [
      catalogBannerBulkBusy,
      updateAvailableSystemTemplateIds,
      templateService,
      uiNotificationService,
    ]
  );

  /** Super-admin: debounced server search (master-templates `search` query). */
  const masterSearchDebounceSkipFirst = useRef(true);
  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }
    if (masterSearchDebounceSkipFirst.current) {
      masterSearchDebounceSkipFirst.current = false;
      return;
    }
    const t = window.setTimeout(() => {
      loadTemplatesRef.current();
    }, 350);
    return () => window.clearTimeout(t);
  }, [searchTerm, isSuperAdmin]);

  /** ris-radiologist (tenant): debounced server search (`GET /ris/api/templates?search=…`). */
  const tenantSearchDebounceSkipFirst = useRef(true);
  useEffect(() => {
    if (isSuperAdmin) {
      return;
    }
    if (tenantSearchDebounceSkipFirst.current) {
      tenantSearchDebounceSkipFirst.current = false;
      return;
    }
    const t = window.setTimeout(() => {
      loadTemplatesRef.current();
    }, 350);
    return () => window.clearTimeout(t);
  }, [searchTerm, isSuperAdmin]);

  const filteredTemplates = useMemo(() => {
    if (isSuperAdmin) {
      return templates;
    }
    return templates.filter(template => {
      const matchesStatus =
        selectedStatus === 'all' ||
        (selectedStatus === 'active' && templateIsActive(template)) ||
        (selectedStatus === 'inactive' && !templateIsActive(template));
      return matchesStatus;
    });
  }, [templates, selectedStatus, isSuperAdmin]);

  /**
   * Figma: one bulk button — ACTIVATE if any selected row is inactive; otherwise Deactivate (all active).
   */
  const tenantBulkSingleStatusAction = useMemo(() => {
    if (!isRisRadiologistTenant || selectedTemplateIds.size === 0) {
      return null;
    }
    const selected = templates.filter(t => selectedTemplateIds.has(t.id));
    if (selected.length === 0) {
      return null;
    }
    const anyInactive = selected.some(t => !templateIsActive(t));
    return {
      risStatus: (anyInactive ? 'ACTIVE' : 'INACTIVE') as 'ACTIVE' | 'INACTIVE',
      /** Figma copy: uppercase ACTIVATE vs sentence-case Deactivate */
      label: anyInactive ? 'ACTIVATE' : 'Deactivate',
      ariaLabel: anyInactive
        ? `Activate ${selected.length} selected template${selected.length === 1 ? '' : 's'}`
        : `Deactivate ${selected.length} selected template${selected.length === 1 ? '' : 's'}`,
    };
  }, [isRisRadiologistTenant, selectedTemplateIds, templates]);

  const paginatedTemplates = useMemo(() => {
    if (isSuperAdmin) {
      return templates;
    }
    const start = (currentPage - 1) * TEMPLATES_ITEMS_PER_PAGE;
    return filteredTemplates.slice(start, start + TEMPLATES_ITEMS_PER_PAGE);
  }, [filteredTemplates, currentPage, templates, isSuperAdmin]);

  const toggleSelectAllOnPage = useCallback(
    (checked: boolean) => {
      setSelectedTemplateIds(prev => {
        const next = new Set(prev);
        paginatedTemplates.forEach(t => {
          if (checked) {
            next.add(t.id);
          } else {
            next.delete(t.id);
          }
        });
        return next;
      });
    },
    [paginatedTemplates]
  );

  const allPageRowsSelected =
    isRisRadiologistTenant &&
    paginatedTemplates.length > 0 &&
    paginatedTemplates.every(t => selectedTemplateIds.has(t.id));

  const getTemplateCategory = (template: Template): string => {
    const cat = template.anatomy_category?.trim();
    return cat || '—';
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedModality, selectedBodyPartId, selectedStatus, selectedTypeFilter]);

  const handleBodyPartChange = (bodyPartId: number | null, bodyPartName: string) => {
    setSelectedBodyPartId(bodyPartId);
  };

  const templateStatusFilterVdOptions: VirtualizedDropdownOption[] = useMemo(
    () => TEMPLATE_STATUS_FILTER_OPTIONS.map((o, i) => ({ id: i + 1, label: o.label })),
    []
  );

  const templateStatusFilterSelectedId = useMemo((): number | null => {
    const idx = TEMPLATE_STATUS_FILTER_OPTIONS.findIndex(o => o.value === selectedStatus);
    return idx >= 0 ? idx + 1 : 1;
  }, [selectedStatus]);

  const handleTemplateStatusFilterChange = (id: number | null, _label: string) => {
    if (id === null) {
      return;
    }
    const opt = TEMPLATE_STATUS_FILTER_OPTIONS[id - 1];
    if (opt) {
      setSelectedStatus(opt.value as 'all' | 'active' | 'inactive');
      setCurrentPage(1);
    }
  };

  const templateModalityFilterVdOptions: VirtualizedDropdownOption[] = useMemo(
    () => TEMPLATE_MODALITY_FILTER_OPTIONS.map((o, i) => ({ id: i + 1, label: o.label })),
    []
  );

  const templateModalityFilterSelectedId = useMemo((): number | null => {
    const idx = TEMPLATE_MODALITY_FILTER_OPTIONS.findIndex(o => o.value === selectedModality);
    return idx >= 0 ? idx + 1 : 1;
  }, [selectedModality]);

  const handleTemplateModalityFilterChange = (id: number | null, _label: string) => {
    if (id === null) {
      return;
    }
    const opt = TEMPLATE_MODALITY_FILTER_OPTIONS[id - 1];
    if (opt) {
      setSelectedModality(opt.value);
      setCurrentPage(1);
    }
  };

  const templateTypeFilterVdOptions: VirtualizedDropdownOption[] = useMemo(
    () => TEMPLATE_TYPE_FILTER_OPTIONS.map((o, i) => ({ id: i + 1, label: o.label })),
    []
  );

  const templateTypeFilterSelectedId = useMemo((): number | null => {
    const idx = TEMPLATE_TYPE_FILTER_OPTIONS.findIndex(o => o.value === selectedTypeFilter);
    return idx >= 0 ? idx + 1 : 1;
  }, [selectedTypeFilter]);

  const handleTemplateTypeFilterChange = (id: number | null, _label: string) => {
    if (id === null) {
      return;
    }
    const opt = TEMPLATE_TYPE_FILTER_OPTIONS[id - 1];
    if (opt) {
      setSelectedTypeFilter(opt.value as 'all' | 'system' | 'custom');
      setCurrentPage(1);
    }
  };

  const handleCreateTemplate = () => {
    openCreateTemplateModal();
  };

  const handleEditTemplate = (template: Template) => {
    setTemplatePanelNonce(n => n + 1);
    // Open directly in edit mode (no "View" + "Edit Template" step; use header X to leave)
    setTemplatePanel({ template, startInViewMode: false });
  };

  const handleCloneTemplate = useCallback(
    async (source: Template) => {
      if (!isRisRadiologistTenant || duplicateTemplateBusyId) {
        return;
      }

      const isDoc = !!source.isDocumentImport;
      const combinedWords = isDoc
        ? getWordCountFromHtml(source.findings || '')
        : getWordCountFromHtml(source.findings || '') +
          getWordCountFromHtml(source.impression || '');
      if (combinedWords > TEMPLATE_MAX_CONTENT_WORDS) {
        uiNotificationService.show({
          title: 'Content Too Large',
          message: `Combined content (Findings + Impression) exceeds the maximum limit of ${TEMPLATE_MAX_CONTENT_WORDS.toLocaleString()} words. Current: ${combinedWords.toLocaleString()} words.`,
          type: 'error',
          duration: 5000,
        });
        return;
      }

      if (source.anatomy_id == null) {
        uiNotificationService.show({
          title: 'Error',
          message: 'Body part is required to duplicate this template.',
          type: 'error',
          duration: 5000,
        });
        return;
      }

      setDuplicateTemplateBusyId(source.id);
      try {
        let modalities = getModalities();
        if (!modalities?.length) {
          await fetchAndStoreModalities();
          modalities = getModalities();
        }
        const modalityId = modalities?.find(m => m.name === source.modality)?.id;
        if (modalityId == null) {
          uiNotificationService.show({
            title: 'Error',
            message: 'Could not resolve modality ID. Refresh the page and try again.',
            type: 'error',
            duration: 5000,
          });
          return;
        }

        const templateDescriptionSuffix =
          source.template_description != null
            ? String(source.template_description).replace(/\s/g, '').trim()
            : '';

        const headerBodyPartLabel = getTemplateBodyPartHeaderLabel(source);
        const namePrefix = buildMasterTemplateAutoDisplayName(
          source.modality,
          headerBodyPartLabel,
          (source.viewLabel ?? '').trim(),
          (source.diagnosisLabel ?? '').trim()
        ).trim();

        let newTemplateName = combineRadiologistTemplateTitle(
          namePrefix,
          templateDescriptionSuffix,
          MASTER_TEMPLATE_NEW_DISPLAY_NAME
        );
        const copySuffix = ' (copy)';
        if (!newTemplateName.endsWith(copySuffix)) {
          newTemplateName = `${newTemplateName}${copySuffix}`;
        }

        const catalogCreatePayload: Omit<Template, 'id' | 'createdAt' | 'updatedAt'> = {
          name: newTemplateName,
          modality: source.modality,
          findings: source.findings || '',
          impression: isDoc ? '' : source.impression || '',
          isDocumentImport: isDoc,
          anatomy_id: source.anatomy_id,
          viewLabel: (source.viewLabel ?? '').trim(),
          diagnosisLabel: (source.diagnosisLabel ?? '').trim(),
          status: 'Active',
          template_description: templateDescriptionSuffix,
          access_level: source.access_level,
          facility_id: source.facility_id,
        };

        const ts = templateService as {
          createTemplate: (
            payload: Omit<Template, 'id' | 'createdAt' | 'updatedAt'>,
            options?: { modalityId?: number; useMasterTemplates?: boolean }
          ) => Promise<Template>;
        };
        await ts.createTemplate(catalogCreatePayload, { modalityId });

        await loadTemplates();
        uiNotificationService.show({
          title: 'Template Created',
          message: 'A duplicate was saved to your templates.',
          type: 'success',
          duration: 3000,
        });
      } catch (error) {
        console.error('Error duplicating template:', error);
        uiNotificationService.show({
          title: 'Error',
          message: error instanceof Error ? error.message : 'Failed to duplicate template',
          type: 'error',
          duration: 5000,
        });
      } finally {
        setDuplicateTemplateBusyId(null);
      }
    },
    [
      isRisRadiologistTenant,
      duplicateTemplateBusyId,
      templateService,
      uiNotificationService,
      loadTemplates,
    ]
  );

  const handleBulkTemplatesStatus = useCallback(
    async (status: 'ACTIVE' | 'INACTIVE') => {
      if (!templateService || !isRisRadiologistTenant || tenantBulkStatusBusy) {
        return;
      }
      const selectedTemplates = templates.filter(t => selectedTemplateIds.has(t.id));
      const hasActive = selectedTemplates.some(t => templateIsActive(t));
      const hasInactive = selectedTemplates.some(t => !templateIsActive(t));
      // ACTIVATE: only inactive rows; DEACTIVATE: only active rows (no mix, no redundant action)
      if (status === 'ACTIVE' && hasActive) {
        uiNotificationService.show({
          title: 'Error',
          message: BULK_TEMPLATE_STATUS_MIXED_SELECTION_MESSAGE,
          type: 'error',
          duration: 5000,
        });
        return;
      }
      if (status === 'INACTIVE' && hasInactive) {
        uiNotificationService.show({
          title: 'Error',
          message: BULK_TEMPLATE_STATUS_MIXED_SELECTION_MESSAGE,
          type: 'error',
          duration: 5000,
        });
        return;
      }

      const ids = [...selectedTemplateIds]
        .map(id => Number(id))
        .filter(id => Number.isFinite(id) && id > 0);
      if (ids.length === 0) {
        return;
      }
      setTenantBulkStatusBusy(true);
      try {
        const ts = templateService as {
          postTemplatesStatus: (p: {
            status: 'ACTIVE' | 'INACTIVE';
            template_ids: number[];
          }) => Promise<void>;
        };
        await ts.postTemplatesStatus({ status, template_ids: ids });
        setSelectedTemplateIds(new Set());
        await loadTemplatesRef.current();
        uiNotificationService.show({
          title: 'Success',
          message:
            status === 'ACTIVE'
              ? `${ids.length} template(s) set to active.`
              : `${ids.length} template(s) set to inactive.`,
          type: 'success',
          duration: 3000,
        });
      } catch (error) {
        console.error('Error updating template status:', error);
        uiNotificationService.show({
          title: 'Error',
          message: error instanceof Error ? error.message : 'Request failed.',
          type: 'error',
          duration: 5000,
        });
      } finally {
        setTenantBulkStatusBusy(false);
      }
    },
    [
      isRisRadiologistTenant,
      selectedTemplateIds,
      templates,
      templateService,
      tenantBulkStatusBusy,
      uiNotificationService,
    ]
  );

  const handleToggleTemplate = async (template: Template, checked: boolean) => {
    try {
      // checked = true → ACTIVE; checked = false → INACTIVE (tenant uses POST /templates/status)

      if (isSuperAdmin) {
        await templateService.updateMasterTemplateDeletedFlag(template.id, !checked);
        await loadTemplates();
        uiNotificationService.show({
          title: checked ? 'Template Activated' : 'Template Deactivated',
          message: checked ? 'The template is now active.' : 'The template is now inactive.',
          type: 'success',
          duration: 3000,
        });
        return;
      }

      const numericId = Number(template.id);
      if (!Number.isFinite(numericId) || numericId <= 0) {
        throw new Error('Invalid template id');
      }

      const ts = templateService as {
        postTemplatesStatus: (p: {
          status: 'ACTIVE' | 'INACTIVE';
          template_id: number;
        }) => Promise<void>;
      };
      await ts.postTemplatesStatus({
        status: checked ? 'ACTIVE' : 'INACTIVE',
        template_id: numericId,
      });

      await loadTemplates();
      uiNotificationService.show({
        title: checked ? 'Template Activated' : 'Template Deactivated',
        message: checked ? 'The template is now active.' : 'The template is now inactive.',
        type: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Error toggling template:', error);
      let errorMessage = 'An error occurred while updating the template';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      uiNotificationService.show({
        title: 'Error',
        message: errorMessage,
        type: 'error',
        duration: 5000,
      });
    }
  };

  const handleImportTemplate = () => {
    const reloadTemplates = () => loadTemplates();

    show({
      content: UploadTemplateModal,
      title: '',
      contentProps: {
        servicesManager,
        hide,
        onJsonImportComplete: reloadTemplates,
        onOpenCreateModals: (
          documents: Array<{ importedContent: string; suggestedName: string }>
        ) => {
          if (!documents.length) {
            return;
          }

          // Close upload modal first, then open the next step (review / create).
          hide();

          if (documents.length > TEMPLATE_BULK_UPLOAD_AUTO_CREATE_THRESHOLD) {
            setTimeout(() => {
              show({
                content: BulkUploadReviewModal,
                title: '',
                showCloseButton: false,
                contentProps: {
                  servicesManager,
                  hide,
                  documents,
                  onCreated: (opts?: { keepOpen?: boolean }) => {
                    if (!opts?.keepOpen) {
                      hide();
                    }
                    loadTemplatesRef.current();
                  },
                },
                containerClassName: BULK_UPLOAD_REVIEW_MODAL_DIALOG_CLASS,
              });
            }, 50);
            return;
          }

          if (documents.length > 1) {
            setTimeout(() => {
              openCreateTemplateModal({
                importedContent: documents[0].importedContent,
                suggestedName: documents[0].suggestedName,
                fromUploadFlow: true,
                batchDocuments: documents,
              });
            }, 50);
            return;
          }

          setTimeout(() => {
            openCreateTemplateModal({
              importedContent: documents[0].importedContent,
              suggestedName: documents[0].suggestedName,
              fromUploadFlow: true,
            });
          }, 50);
        },
      },
      containerClassName: UPLOAD_TEMPLATE_MODAL_DIALOG_CLASS,
    });
  };

  const handleOpenImportFromLibrary = () => {
    show({
      content: ImportMasterCatalogModal,
      title: '',
      showCloseButton: false,
      contentProps: {
        servicesManager,
        hide,
        onImported: () => {
          loadTemplatesRef.current();
        },
      },
      containerClassName: IMPORT_LIBRARY_MODAL_DIALOG_CLASS,
    });
  };

  const effectiveUserForTemplates = user ?? authService.getUser();
  if (
    effectiveUserForTemplates != null &&
    !canAccessPage(effectiveUserForTemplates, '/templates')
  ) {
    return <Unauthorized />;
  }

  return (
    <PageLayout
      title=""
      hideTitleInHeader
      breadcrumbs={[
        {
          label: 'Home',
          path: hasRole(effectiveUserForTemplates, ROLES.SUPER_ADMIN) ? '/facilities' : '/admin-dashboard',
        },
        { label: 'Templates' },
      ]}
    >
      <div className="templates-page-root">
        <Onboarding />
        <div className="templates-page-container templates-page-container--scroll">
          <div className="templates-split">
            <div className="templates-split-main">
              {/* Title + actions live in the main column only so buttons align with Figma when the drawer is open */}
              <div className="templates-page-subheader sticky top-0 z-20 shrink-0 bg-white shadow-sm">
                <div className="templates-page-subheader-row flex h-14 items-center justify-between border-b border-[#e5e5e5] px-6">
                  <h1 className="templates-page-subheader-title m-0 truncate text-[18px] font-bold leading-[28px] text-[#0a0a0a]">
                    Templates
                  </h1>
                  <div className="templates-subheader-actions">
                    {isRisRadiologistTenant ? (
                      <button
                        type="button"
                        className="templates-btn-outline"
                        onClick={handleOpenImportFromLibrary}
                        title="Import templates from platform catalog"
                      >
                        <Library
                          size={18}
                          strokeWidth={ICON_STROKE_WIDTH}
                        />
                        Import Templates
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="templates-btn-outline"
                      onClick={handleImportTemplate}
                      title={`Import multiple files or a folder — ${SUPPORTED_FILE_TYPES.join(', ')}`}
                    >
                      <Upload
                        size={18}
                        strokeWidth={ICON_STROKE_WIDTH}
                      />
                      Upload Template
                    </button>
                    <button
                      type="button"
                      className="user-management-subheader-add-btn"
                      onClick={handleCreateTemplate}
                    >
                      <Plus
                        strokeWidth={ICON_STROKE_WIDTH}
                        size={18}
                      />
                      New Template
                    </button>
                  </div>
                </div>
              </div>
              <div className="templates-page-inner templates-page-inner--scroll">
                {isRisRadiologistTenant && totalCatalogUpdatesAvailable > 0 ? (
                  <div
                    role="alert"
                    className="templates-catalog-update-banner"
                  >
                    <RefreshCw
                      className="templates-catalog-update-banner__icon"
                      strokeWidth={ICON_STROKE_WIDTH}
                      aria-hidden
                    />
                    <div className="templates-catalog-update-banner__body">
                      <div className="templates-catalog-update-banner__title">Update available</div>
                      <div className="templates-catalog-update-banner__row">
                        <p className="templates-catalog-update-banner__text">
                          {totalCatalogUpdatesAvailable === 1
                            ? 'Super Admin published a newer version of one imported system template.'
                            : `Super Admin published a newer version of ${totalCatalogUpdatesAvailable} imported system templates.`}
                        </p>
                        <div className="templates-catalog-update-banner__actions">
                          <button
                            type="button"
                            disabled={catalogBannerBulkBusy}
                            onClick={() => void handleCatalogUpdateBannerBulk('UPDATE')}
                            className="templates-catalog-update-banner__btn templates-catalog-update-banner__btn--primary"
                          >
                            {catalogBannerBulkBusy ? 'Working…' : 'Accept'}
                          </button>
                          <button
                            type="button"
                            disabled={catalogBannerBulkBusy}
                            onClick={() => void handleCatalogUpdateBannerBulk('KEEP_CURRENT')}
                            className="templates-catalog-update-banner__btn templates-catalog-update-banner__btn--outline"
                          >
                            Decline
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
                <div
                  className="user-management-header user-management-filters-inline templates-page--um-filters"
                  role="search"
                  aria-label="Template filters"
                >
                  <div className="user-management-header-left">
                    <div className="user-management-filters">
                      <div className="user-management-filter-wrapper user-management-filter-search">
                        <span
                          className="user-management-search-icon"
                          aria-hidden
                        >
                          <Search
                            size={18}
                            strokeWidth={ICON_STROKE_WIDTH}
                          />
                        </span>
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={e => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                          }}
                          placeholder="Search"
                          className="user-management-search-input"
                          aria-label="Search templates by name or modality"
                        />
                      </div>
                      <div className="user-management-filter-wrapper user-management-filter-wrapper-status templates-filter-dd-wrap">
                        <VirtualizedDropdown
                          value={templateStatusFilterSelectedId}
                          onChange={handleTemplateStatusFilterChange}
                          options={templateStatusFilterVdOptions}
                          placeholder="All status"
                          showNone={false}
                          showSearch={false}
                          containerClassName="templates-filter-vd-wrap w-full min-w-0"
                          className="templates-filter-vd-trigger font-medium"
                          variant="light"
                        />
                      </div>
                      <div className="user-management-filter-wrapper user-management-filter-wrapper-role templates-filter-dd-wrap">
                        <VirtualizedDropdown
                          value={templateModalityFilterSelectedId}
                          onChange={handleTemplateModalityFilterChange}
                          options={templateModalityFilterVdOptions}
                          placeholder="All modalities"
                          showNone={false}
                          showSearch={false}
                          containerClassName="templates-filter-vd-wrap w-full min-w-0"
                          className="templates-filter-vd-trigger font-medium"
                          variant="light"
                        />
                      </div>
                      <div className="user-management-filter-wrapper templates-filter-category-wrap templates-filter-dd-wrap">
                        <BodyPartDropdown
                          modality={selectedModality || undefined}
                          value={selectedBodyPartId}
                          onChange={handleBodyPartChange}
                          placeholder="All body parts"
                          noneLabel="All body parts"
                          showNone={true}
                          containerClassName="templates-filter-vd-wrap w-full min-w-0"
                          className="templates-filter-vd-trigger font-medium"
                          variant="light"
                          searchPlaceholder="Search..."
                        />
                      </div>
                      {isRisRadiologistTenant ? (
                        <div className="user-management-filter-wrapper templates-filter-category-wrap templates-filter-dd-wrap">
                          <VirtualizedDropdown
                            value={templateTypeFilterSelectedId}
                            onChange={handleTemplateTypeFilterChange}
                            options={templateTypeFilterVdOptions}
                            placeholder="All types"
                            showNone={false}
                            showSearch={false}
                            containerClassName="templates-filter-vd-wrap w-full min-w-0"
                            className="templates-filter-vd-trigger font-medium"
                            variant="light"
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="user-management-header-right" />
                </div>

                {tenantBulkSingleStatusAction ? (
                  <div className="templates-bulk-action-row">
                    <button
                      type="button"
                      className="templates-bulk-action-btn templates-btn-outline"
                      disabled={tenantBulkStatusBusy}
                      aria-label={tenantBulkSingleStatusAction.ariaLabel}
                      onClick={() =>
                        void handleBulkTemplatesStatus(tenantBulkSingleStatusAction.risStatus)
                      }
                    >
                      {tenantBulkSingleStatusAction.label}
                    </button>
                  </div>
                ) : null}

                <div className="templates-table-card templates-table-card--scroll">
                  {filteredTemplates.length === 0 ? (
                    <div className="templates-empty templates-empty--fill">
                      <Icons.ListView className="templates-empty-icon mx-auto mb-3 h-12 w-12 opacity-40" />
                      {templatesMessage ||
                        (searchTerm ||
                        selectedModality ||
                        selectedBodyPartId ||
                        selectedStatus !== 'all'
                          ? 'No templates found matching your criteria.'
                          : 'No templates yet. Upload or create a template to get started.')}
                    </div>
                  ) : (
                    <div className="templates-table-wrap templates-table-wrap--figma">
                      <table className="templates-table templates-table--figma">
                        <thead>
                          <tr>
                            {isRisRadiologistTenant ? (
                              <th
                                className="templates-th-checkbox"
                                scope="col"
                              >
                                <input
                                  type="checkbox"
                                  className="templates-table-row-checkbox"
                                  aria-label="Select all templates on this page"
                                  checked={allPageRowsSelected}
                                  onChange={e => toggleSelectAllOnPage(e.target.checked)}
                                />
                              </th>
                            ) : null}
                            <th className="templates-th-serial">S.No.</th>
                            <th className="templates-th-name">
                              <span className="templates-th-name-inner">
                                Template name
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      className="templates-th-info"
                                      aria-label="About template name"
                                    >
                                      <Icons.Info className="templates-th-info-icon" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="top"
                                    className="max-w-xs text-xs"
                                  >
                                    Full template title as stored in RIS; row opens view/edit.
                                  </TooltipContent>
                                </Tooltip>
                              </span>
                            </th>
                            {isRisRadiologistTenant ? (
                              <th
                                className="templates-th-type"
                                scope="col"
                              >
                                Type
                              </th>
                            ) : null}
                            <th className="templates-th-modality">Modality</th>
                            <th className="templates-th-body-part">Body part</th>
                            <th className="templates-th-view">View</th>
                            <th className="templates-th-diagnosis">Diagnosis</th>
                            <th className="templates-th-version">Version</th>
                            {isSuperAdmin ? (
                              <th
                                className="templates-th-mod-by"
                                scope="col"
                              >
                                Last modified by
                              </th>
                            ) : null}
                            <th className="templates-th-mod-at">Last modified at</th>
                            <th className="templates-th-status">Status</th>
                            {isRisRadiologistTenant ? (
                              <th
                                className="templates-th-clone"
                                scope="col"
                              >
                                <span className="sr-only">Duplicate</span>
                              </th>
                            ) : null}
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedTemplates.map((template, rowIdx) => {
                            const serial =
                              (currentPage - 1) * TEMPLATES_ITEMS_PER_PAGE + rowIdx + 1;
                            const bodyPartLabel = getTemplateCategory(template);
                            const versionNum = template.version ?? 1;
                            return (
                              <tr
                                key={template.id}
                                className={cn(
                                  'templates-table-row templates-table-row--clickable',
                                  templatePanel &&
                                    templatePanel.template.id === template.id &&
                                    'templates-table-row--selected'
                                )}
                                onClick={() => handleEditTemplate(template)}
                              >
                                {isRisRadiologistTenant ? (
                                  <td
                                    className="templates-td-checkbox"
                                    onClick={e => e.stopPropagation()}
                                  >
                                    <input
                                      type="checkbox"
                                      className="templates-table-row-checkbox"
                                      aria-label={`Select template ${serial}`}
                                      checked={selectedTemplateIds.has(template.id)}
                                      onChange={e =>
                                        toggleTemplateRowSelected(template.id, e.target.checked)
                                      }
                                    />
                                  </td>
                                ) : null}
                                <td className={cn('templates-td-serial', 'font-medium')}>{serial}</td>
                                <td className="templates-col-name templates-td-name-figma templates-td-name-cell">
                                  <div className="templates-td-name-row">
                                    <span
                                      className={cn('templates-td-name-text', 'font-medium')}
                                      title={templateRowDisplayName(template)}
                                    >
                                      {templateRowDisplayName(template)}
                                    </span>
                                    {isRisRadiologistTenant && template.updateAvailable ? (
                                      <button
                                        type="button"
                                        className="templates-badge-update-available templates-badge-update-available--btn"
                                        aria-label={`View catalogue update for ${templateRowDisplayName(template)}`}
                                        onClick={e => {
                                          e.stopPropagation();
                                          openViewUpdateModal(template.id);
                                        }}
                                      >
                                        Update available
                                      </button>
                                    ) : null}
                                  </div>
                                </td>
                                {isRisRadiologistTenant ? (
                                  <td className="templates-td-type templates-td-nowrap">
                                    <span
                                      className={cn(
                                        'templates-badge templates-badge-type',
                                        templateListTypeDisplay(template) === 'System'
                                          ? 'templates-badge-type--system'
                                          : 'templates-badge-type--custom'
                                      )}
                                    >
                                      {templateListTypeDisplay(template)}
                                    </span>
                                  </td>
                                ) : null}
                                <td className="templates-td-modality">
                                  <span className="templates-badge templates-badge--figma-secondary">
                                    {modalityBadgeLabel(template.modality)}
                                  </span>
                                </td>
                                <td className="templates-td-nowrap templates-td-body-part">
                                  {bodyPartLabel}
                                </td>
                                <td className="templates-td-muted templates-td-nowrap templates-td-view">
                                  {templateViewDisplay(template)}
                                </td>
                                <td className="templates-td-muted templates-td-ellipsis templates-td-diagnosis">
                                  {templateDiagnosisDisplay(template)}
                                </td>
                                <td className="templates-td-version">v{versionNum}</td>
                                {isSuperAdmin ? (
                                  <td className="templates-td-nowrap">
                                    {templateModifiedByDisplay(template)}
                                  </td>
                                ) : null}
                                <td className="templates-td-muted templates-td-datetime templates-font-tabular">
                                  {formatTemplateDateTime(template.updatedAt)}
                                </td>
                                <td
                                  className="templates-td-status"
                                  onClick={e => e.stopPropagation()}
                                >
                                  <ToggleSwitch
                                    checked={template.is_deleted !== true}
                                    onChange={checked => handleToggleTemplate(template, checked)}
                                  />
                                </td>
                                {isRisRadiologistTenant ? (
                                  <td
                                    className="templates-td-clone"
                                    onClick={e => e.stopPropagation()}
                                  >
                                    <button
                                      type="button"
                                      className="templates-btn-clone"
                                      title="Duplicate template"
                                      aria-label="Duplicate template"
                                      disabled={duplicateTemplateBusyId !== null}
                                      onClick={() => {
                                        void handleCloneTemplate(template);
                                      }}
                                    >
                                      <Copy
                                        size={18}
                                        strokeWidth={ICON_STROKE_WIDTH}
                                        aria-hidden
                                      />
                                    </button>
                                  </td>
                                ) : null}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="user-management-pagination">
                  <Pagination
                    currentPage={currentPage}
                    totalItems={isSuperAdmin ? masterListTotalItems : filteredTemplates.length}
                    itemsPerPage={TEMPLATES_ITEMS_PER_PAGE}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </div>
            </div>

            {templatePanel && (
              <aside
                className="templates-detail-panel"
                aria-label="Template details"
              >
                <CreateTemplateModal
                  key={`panel-${templatePanelNonce}-edit-${templatePanel.template.id}`}
                  embedded
                  template={templatePanel.template}
                  startInViewMode={templatePanel.startInViewMode}
                  onSave={() => {
                    closeTemplatePanel();
                    loadTemplates();
                  }}
                  servicesManager={servicesManager}
                  hide={closeTemplatePanel}
                />
              </aside>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

type TemplateServiceWithMasterCatalog = {
  fetchMasterTemplateCatalog: () => Promise<MasterCatalogItem[]>;
  importTemplatesFromMasterCatalog: (ids: number[]) => Promise<void>;
};

/** Figma: Import from library — no remaining catalog rows to import */
const IMPORT_LIBRARY_NOTHING_NEW_TITLE = 'Nothing new to import';
const IMPORT_LIBRARY_NOTHING_NEW_MESSAGE =
  'Every library template is already in your list.';

/** RIS radiologist: “Import from library” — GET master catalog, import selected or all */
function ImportMasterCatalogModal({
  servicesManager,
  hide,
  onImported,
}: {
  servicesManager: ServicesManager;
  hide?: () => void;
  onImported: () => void | Promise<void>;
}) {
  const { templateService, uiNotificationService } = servicesManager.services;
  const ts = templateService as unknown as TemplateServiceWithMasterCatalog;

  const [items, setItems] = useState<MasterCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await ts.fetchMasterTemplateCatalog();
      setItems(rows);
      setSelectedIds(new Set());
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load catalog');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [ts]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const importableIds = useMemo(() => items.filter(i => !i.imported).map(i => i.id), [items]);

  const toggleRow = (id: number, imported: boolean) => {
    if (imported || importing) {
      return;
    }
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const runImport = async (
    ids: number[],
    options?: { closeOnSuccess?: boolean }
  ) => {
    if (importing) {
      return;
    }
    if (ids.length === 0) {
      const catalogLoaded = items.length > 0 && !loadError;
      const everyAlreadyImported = items.every(i => i.imported);
      if (catalogLoaded && everyAlreadyImported) {
        uiNotificationService.show({
          title: IMPORT_LIBRARY_NOTHING_NEW_TITLE,
          message: IMPORT_LIBRARY_NOTHING_NEW_MESSAGE,
          type: 'info',
          duration: 4500,
        });
      }
      return;
    }
    setImporting(true);
    try {
      await ts.importTemplatesFromMasterCatalog(ids);
      uiNotificationService.show({
        title: 'Import successful',
        message:
          ids.length === 1
            ? 'One template was imported.'
            : `${ids.length} templates were imported.`,
        type: 'success',
        duration: 4000,
      });
      await loadCatalog();
      await onImported();
      if (options?.closeOnSuccess) {
        hide?.();
      }
    } catch (e) {
      uiNotificationService.show({
        title: 'Import failed',
        message: e instanceof Error ? e.message : 'Could not import templates.',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setImporting(false);
    }
  };

  const selectedImportableCount = useMemo(() => {
    let n = 0;
    selectedIds.forEach(id => {
      const row = items.find(r => r.id === id);
      if (row && !row.imported) {
        n++;
      }
    });
    return n;
  }, [items, selectedIds]);

  const importSelectedDisabled = selectedImportableCount === 0 || importing;
  /** Import all stays clickable when everything is already imported so we can show Figma “nothing new” toast */
  const importAllDisabled = importing || loading || !!loadError || items.length === 0;

  return (
    <div className="import-library-modal">
      <div className="import-library-modal__header">
        <div className="import-library-modal__header-main">
          <div className="import-library-modal__title-row">
            <h2
              className="create-template-modal__title import-library-modal__title"
              id="import-library-modal-title"
            >
              Import from library
            </h2>
            <button
              type="button"
              className="import-library-modal__close"
              onClick={() => hide?.()}
              disabled={importing}
              aria-label="Close"
            >
              <X
                size={20}
                strokeWidth={ICON_STROKE_WIDTH}
              />
            </button>
          </div>
          <p className="import-library-modal__intro">
            Choose platform templates published by Super Admin. Import all at once, or pick specific
            templates. Names follow Modality — Body part — View — Diagnosis.
          </p>
        </div>
      </div>

      <div className="import-library-modal__body">
        {loading ? (
          <p className="import-library-modal__muted">Loading catalog…</p>
        ) : loadError ? (
          <p className="import-library-modal__error">{loadError}</p>
        ) : items.length === 0 ? (
          <p className="import-library-modal__muted">No templates available to import.</p>
        ) : (
          <ul
            className="import-library-modal__list"
            aria-labelledby="import-library-modal-title"
          >
            {items.map(row => {
              const displayName = formatTemplateNameForListDisplay(row.template_name);
              const versionLabel = `v${row.version}`;
              const checked = selectedIds.has(row.id);
              const isSelected = checked && !row.imported;
              return (
                <li
                  key={row.id}
                  className={cn(
                    'import-library-modal__row',
                    row.imported && 'import-library-modal__row--imported',
                    isSelected && 'import-library-modal__row--selected'
                  )}
                >
                  {row.imported ? (
                    <div
                      className="import-library-modal__row-inner import-library-modal__row-inner--imported"
                      aria-disabled
                    >
                      <span
                        className="import-library-modal__checkbox-spacer"
                        aria-hidden
                      />
                      <div className="import-library-modal__row-text">
                        <span className="import-library-modal__row-name">{displayName}</span>
                        <div className="import-library-modal__row-meta-line">
                          <span className="import-library-modal__row-version">{versionLabel}</span>
                          <span className="import-library-modal__row-imported-label">
                            Already imported
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <label className="import-library-modal__row-inner">
                      <input
                        type="checkbox"
                        className="import-library-modal__checkbox"
                        checked={checked}
                        disabled={importing}
                        onChange={() => toggleRow(row.id, row.imported)}
                      />
                      <div className="import-library-modal__row-text">
                        <span className="import-library-modal__row-name">{displayName}</span>
                        <div className="import-library-modal__row-meta-line">
                          <span className="import-library-modal__row-version">{versionLabel}</span>
                        </div>
                      </div>
                    </label>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="create-template-modal__footer import-library-modal__footer">
        <div className="import-library-modal__footer-inner">
          <button
            type="button"
            className="user-management-subheader-add-btn"
            disabled={importAllDisabled}
            onClick={() => runImport(importableIds, { closeOnSuccess: true })}
          >
            Import all
          </button>
          <div className="import-library-modal__footer-right">
            <button
              type="button"
              className="templates-btn-outline import-library-modal__footer-btn"
              onClick={() => hide?.()}
              disabled={importing}
            >
              Cancel
            </button>
            <button
              type="button"
              className="templates-btn-outline import-library-modal__footer-btn"
              disabled={importSelectedDisabled}
              onClick={() => runImport([...selectedIds].filter(id => importableIds.includes(id)))}
            >
              Import selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Bulk upload review — >5 files / large folder: list real names + per-file modality, then create all. */
interface BulkUploadReviewModalProps {
  servicesManager: ServicesManager;
  hide?: () => void;
  documents: Array<{ importedContent: string; suggestedName: string }>;
  /** Full success / all-duplicates done: refresh list and close. Pass keepOpen to refresh without closing. */
  onCreated: (opts?: { keepOpen?: boolean }) => void;
}

function modalityCodeToDropdownId(modalityCode: string): number | null {
  const idx = TEMPLATE_MODALITY_OPTIONS.findIndex(o => o.value === modalityCode);
  return idx >= 0 ? idx + 1 : null;
}

/**
 * Name upload create actually persists — must match duplicate-key input vs `template.name`.
 * Super-admin master: explicit `template_name` = unique file basename.
 * Tenant catalog: API builds from modality + body + view + diagnosis; file basename goes in diagnosis.
 */
function resolveUploadPersistedName(params: {
  isSuperAdmin: boolean;
  fileName: string;
  modality: string;
  bodyPartLabel?: string;
  view?: string;
  descriptionSuffix?: string;
}): string {
  const fileName = (params.fileName || 'Upload').trim() || 'Upload';
  if (params.isSuperAdmin) {
    return fileName;
  }
  const prefix = buildMasterTemplateAutoDisplayName(
    params.modality,
    params.bodyPartLabel || '',
    params.view || '',
    fileName
  ).trim();
  return combineRadiologistTemplateTitle(prefix, params.descriptionSuffix || '', fileName);
}

async function fetchExistingUploadDuplicateKeys(
  templateService: {
    fetchTemplatesFromApi: (options?: {
      page?: number;
      limit?: number;
      templatesListBaseUrl?: string;
    }) => Promise<{ templates: Template[]; totalPages?: number; page?: number }>;
  },
  isSuperAdmin: boolean
): Promise<Set<string>> {
  const existingKeys = new Set<string>();
  try {
    const listOpts = {
      limit: TEMPLATES_API_FETCH_LIMIT,
      ...(isSuperAdmin ? { templatesListBaseUrl: API_ENDPOINTS.MASTER_TEMPLATES.GET } : {}),
    };
    let page = 1;
    let totalPages = 1;
    while (page <= totalPages && page <= TEMPLATE_UPLOAD_DUPLICATE_CHECK_MAX_PAGES) {
      const existingList = await templateService.fetchTemplatesFromApi({
        ...listOpts,
        page,
      });
      for (const t of existingList.templates || []) {
        // Match backend active identity: LOWER(name) + modality; inactive names may be reused.
        if (t?.name && !t.is_deleted && t.status !== 'Inactive') {
          existingKeys.add(uploadDuplicateKey(String(t.name), t.modality));
        }
      }
      const reportedPages = Number(existingList.totalPages);
      totalPages =
        Number.isFinite(reportedPages) && reportedPages > 0
          ? reportedPages
          : (existingList.templates || []).length < TEMPLATES_API_FETCH_LIMIT
            ? page
            : page + 1;
      if (!(existingList.templates || []).length) {
        break;
      }
      page += 1;
    }
  } catch {
    // Best-effort; within-batch and API duplicate handling still apply.
  }
  return existingKeys;
}

function BulkUploadReviewModal({
  servicesManager,
  hide,
  documents,
  onCreated,
}: BulkUploadReviewModalProps) {
  const user = useAuthUser();
  const isSuperAdmin = hasRole(user, ROLES.SUPER_ADMIN);
  const { templateService, uiNotificationService } = servicesManager.services as {
    templateService: {
      createTemplate: (
        template: Omit<Template, 'id' | 'createdAt' | 'updatedAt'>,
        options?: { useMasterTemplates?: boolean; modalityId?: number }
      ) => Promise<Template>;
      fetchTemplatesFromApi: (options?: {
        page?: number;
        limit?: number;
        templatesListBaseUrl?: string;
      }) => Promise<{ templates: Template[] }>;
    };
    uiNotificationService: {
      show: (opts: {
        title: string;
        message: string;
        type: string;
        duration?: number;
      }) => void;
    };
  };

  const [rows, setRows] = useState(() =>
    documents.map((doc, index) => ({
      id: `${doc.suggestedName}-${index}`,
      importedContent: doc.importedContent,
      suggestedName: doc.suggestedName,
      resolvedFileName: undefined as string | undefined,
    }))
  );
  const [modalitiesByIndex, setModalitiesByIndex] = useState<string[]>(() =>
    documents.map(() => '')
  );
  const [modalityErrors, setModalityErrors] = useState<boolean[]>(() =>
    documents.map(() => false)
  );
  const [duplicateErrors, setDuplicateErrors] = useState<boolean[]>(() =>
    documents.map(() => false)
  );
  const [bodyPartsByIndex, setBodyPartsByIndex] = useState<
    Array<{ id: number | null; label: string }>
  >(() => documents.map(() => ({ id: null, label: '' })));
  const [bodyPartErrors, setBodyPartErrors] = useState<boolean[]>(() =>
    documents.map(() => false)
  );
  const [existingDuplicateKeys, setExistingDuplicateKeys] = useState<Set<string>>(
    () => new Set()
  );
  const [isSaving, setIsSaving] = useState(false);

  const displayNames = useMemo(() => uniqueBulkTemplateNames(rows), [rows]);

  const modalityDropdownOptions: VirtualizedDropdownOption[] = useMemo(
    () =>
      TEMPLATE_MODALITY_OPTIONS.map((o, i) => ({
        id: i + 1,
        label: o.label,
        value: o.value,
      })),
    []
  );

  const persistedNameForRow = useCallback(
    (index: number, modalityCode: string): string =>
      resolveUploadPersistedName({
        isSuperAdmin,
        fileName: displayNames[index] || rows[index]?.suggestedName || 'Upload',
        modality: modalityCode,
        bodyPartLabel: bodyPartsByIndex[index]?.label || '',
      }),
    [isSuperAdmin, displayNames, rows, bodyPartsByIndex]
  );

  const findDuplicateRowFlags = useCallback(
    (modalities: string[]): boolean[] => {
      const flags = rows.map(() => false);
      const identitiesForDup: Array<{ templateName: string; modality: string }> = [];
      const indexMap: number[] = [];

      for (let i = 0; i < rows.length; i += 1) {
        const modalityCode = (modalities[i] || '').trim();
        if (!modalityCode) {
          continue;
        }
        identitiesForDup.push({
          templateName: persistedNameForRow(i, modalityCode),
          modality: modalityCode,
        });
        indexMap.push(i);
      }

      const batchFlags = findWithinBatchDuplicateFlags(identitiesForDup, existingDuplicateKeys);
      batchFlags.forEach((flag, j) => {
        if (flag) {
          flags[indexMap[j]] = true;
        }
      });
      return flags;
    },
    [rows, existingDuplicateKeys, persistedNameForRow]
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const keys = await fetchExistingUploadDuplicateKeys(templateService, isSuperAdmin);
      if (!cancelled) {
        setExistingDuplicateKeys(keys);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [templateService, isSuperAdmin]);

  useEffect(() => {
    setDuplicateErrors(findDuplicateRowFlags(modalitiesByIndex));
  }, [existingDuplicateKeys, findDuplicateRowFlags, modalitiesByIndex]);

  const handleRemoveRow = (index: number) => {
    if (isSaving) {
      return;
    }
    setRows(prev => prev.filter((_, i) => i !== index));
    setModalitiesByIndex(prev => prev.filter((_, i) => i !== index));
    setModalityErrors(prev => prev.filter((_, i) => i !== index));
    setDuplicateErrors(prev => prev.filter((_, i) => i !== index));
    setBodyPartsByIndex(prev => prev.filter((_, i) => i !== index));
    setBodyPartErrors(prev => prev.filter((_, i) => i !== index));
  };

  const handleRowModalityChange = (index: number, id: number | null) => {
    setModalitiesByIndex(prev => {
      const next = [...prev];
      if (id == null) {
        next[index] = '';
      } else {
        const opt = TEMPLATE_MODALITY_OPTIONS[id - 1];
        next[index] = opt ? opt.value : '';
      }
      return next;
    });
    setModalityErrors(prev => {
      const next = [...prev];
      next[index] = false;
      return next;
    });
    if (!isSuperAdmin) {
      setBodyPartsByIndex(prev => {
        const next = [...prev];
        next[index] = { id: null, label: '' };
        return next;
      });
      setBodyPartErrors(prev => {
        const next = [...prev];
        next[index] = false;
        return next;
      });
    }
  };

  const handleSave = async () => {
    if (!rows.length) {
      uiNotificationService.show({
        title: 'No files',
        message: 'All files were removed. Add files again to continue.',
        type: 'info',
        duration: 4000,
      });
      return;
    }

    const missing = modalitiesByIndex.map(m => !m.trim());
    if (missing.some(Boolean)) {
      setModalityErrors(missing);
      uiNotificationService.show({
        title: 'Modality required',
        message: 'Select a modality for each file before saving.',
        type: 'error',
        duration: 4000,
      });
      return;
    }

    // Tenant catalog create requires anatomy_id per row. Super-admin explicit template_name does not.
    if (!isSuperAdmin) {
      const missingBodyParts = bodyPartsByIndex.map((bp, i) => !modalitiesByIndex[i]?.trim() || bp.id == null);
      if (missingBodyParts.some(Boolean)) {
        setBodyPartErrors(missingBodyParts);
        uiNotificationService.show({
          title: 'Body part required',
          message: 'Select a body part for each file before saving.',
          type: 'error',
          duration: 4000,
        });
        return;
      }
    }

    const freshExistingKeys = await fetchExistingUploadDuplicateKeys(
      templateService,
      isSuperAdmin
    );
    setExistingDuplicateKeys(freshExistingKeys);

    const dupFlags = (() => {
      const identities = rows.map((_, i) => {
        const modalityCode = modalitiesByIndex[i];
        return {
          templateName: resolveUploadPersistedName({
            isSuperAdmin,
            fileName: displayNames[i] || rows[i].suggestedName || 'Upload',
            modality: modalityCode,
            bodyPartLabel: bodyPartsByIndex[i]?.label || '',
          }),
          modality: modalityCode,
        };
      });
      return findWithinBatchDuplicateFlags(identities, freshExistingKeys);
    })();

    if (dupFlags.some(Boolean)) {
      setDuplicateErrors(dupFlags);
      uiNotificationService.show({
        title: 'Duplicate template',
        message:
          'A template with the same name already exists for that modality (or is selected twice). Rename, change modality, remove the duplicate file, or skip it.',
        type: 'error',
        duration: 6000,
      });
      return;
    }

    setIsSaving(true);
    try {
      let modalities = getModalities();
      if (!modalities?.length) {
        await fetchAndStoreModalities();
        modalities = getModalities();
      }

      // Lock unique basenames before create so partial retry cannot rename remaining rows.
      const lockedRows = withResolvedBulkFileNames(rows);
      setRows(lockedRows);

      let success = 0;
      let errors = 0;
      let skippedDuplicates = 0;
      const seenInBatch = new Set<string>(freshExistingKeys);
      const failedIndices: number[] = [];

      for (let i = 0; i < lockedRows.length; i += 1) {
        const doc = lockedRows[i];
        const fileName = doc.resolvedFileName;
        const rowModality = modalitiesByIndex[i];
        const rowBodyPart = bodyPartsByIndex[i];
        const persistedName = resolveUploadPersistedName({
          isSuperAdmin,
          fileName,
          modality: rowModality,
          bodyPartLabel: rowBodyPart?.label || '',
        });
        const dupKey = uploadDuplicateKey(persistedName, rowModality);

        if (seenInBatch.has(dupKey)) {
          skippedDuplicates += 1;
          continue;
        }

        const modalityId = modalities?.find(m => m.name === rowModality)?.id;
        if (modalityId == null) {
          errors += 1;
          failedIndices.push(i);
          continue;
        }

        try {
          const payload: Omit<Template, 'id' | 'createdAt' | 'updatedAt'> = {
            name: persistedName,
            modality: rowModality as Template['modality'],
            findings: doc.importedContent,
            impression: '',
            isDocumentImport: true,
            status: 'Active',
            diagnosisLabel: isSuperAdmin ? '' : fileName,
            viewLabel: '',
            ...(!isSuperAdmin && rowBodyPart?.id != null ? { anatomy_id: rowBodyPart.id } : {}),
          };

          if (isSuperAdmin) {
            await templateService.createTemplate(payload, {
              useMasterTemplates: true,
              modalityId,
            });
          } else {
            await templateService.createTemplate(payload, { modalityId });
          }
          seenInBatch.add(dupKey);
          success += 1;
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          if (/already exists|duplicate|unique/i.test(message)) {
            skippedDuplicates += 1;
            seenInBatch.add(dupKey);
          } else {
            console.error(`Bulk upload failed for ${persistedName}:`, error);
            errors += 1;
            failedIndices.push(i);
          }
        }
      }

      if (success === 0 && skippedDuplicates === 0 && errors === 0) {
        uiNotificationService.show({
          title: 'Import Failed',
          message: 'Failed to create templates. Please try again.',
          type: 'error',
          duration: 5000,
        });
        return;
      }

      if (success === 0 && errors === 0 && skippedDuplicates > 0) {
        uiNotificationService.show({
          title: 'Duplicates skipped',
          message: `All ${skippedDuplicates} file(s) were skipped because a template with the same name already exists.`,
          type: 'info',
          duration: 5000,
        });
        onCreated();
        return;
      }

      // Partial API failures: keep failed rows + locked names so the user can retry.
      if (errors > 0 && failedIndices.length > 0) {
        setRows(failedIndices.map(idx => lockedRows[idx]));
        setModalitiesByIndex(failedIndices.map(idx => modalitiesByIndex[idx]));
        setModalityErrors(failedIndices.map(() => false));
        setDuplicateErrors(failedIndices.map(() => false));
        setBodyPartsByIndex(failedIndices.map(idx => bodyPartsByIndex[idx] ?? { id: null, label: '' }));
        setBodyPartErrors(failedIndices.map(() => false));
        uiNotificationService.show({
          title: success > 0 ? 'Partial import' : 'Import Failed',
          message: `${success > 0 ? `Created ${success}, ` : ''}${errors} failed — failed files remain below so you can retry.${
            skippedDuplicates > 0 ? ` ${skippedDuplicates} duplicate(s) skipped.` : ''
          }`,
          type: success > 0 ? 'info' : 'error',
          duration: 7000,
        });
        onCreated({ keepOpen: true });
        return;
      }

      uiNotificationService.show({
        title: 'Templates Created',
        message: `Created ${success} template(s)${
          skippedDuplicates > 0 ? `, ${skippedDuplicates} duplicate(s) skipped` : ''
        }.`,
        type: 'success',
        duration: 5000,
      });
      onCreated();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bulk-upload-review-modal">
      <div className="create-template-modal__header">
        <h2
          className="create-template-modal__title"
          id="bulk-upload-review-modal-title"
        >
          Review uploaded files
        </h2>
      </div>

      <div className="create-template-modal__body bulk-upload-review-modal__body">
        <p className="upload-template-modal__intro">
          {rows.length} file{rows.length === 1 ? '' : 's'} ready to import. Select a modality
          {isSuperAdmin ? '' : ' and body part'} for each file, then Save to create one template per
          file using the names below. The same template name cannot be created twice for the same
          modality.
        </p>

        <div
          className={cn(
            'bulk-upload-review-modal__list-wrap',
            !isSuperAdmin && 'bulk-upload-review-modal__list-wrap--with-body-part'
          )}
        >
          <div className="bulk-upload-review-modal__list-heading bulk-upload-review-modal__list-heading--grid">
            <span>File name</span>
            <span>
              Modality{' '}
              <span
                className="create-template-modal__required-mark"
                aria-hidden
              >
                *
              </span>
            </span>
            {!isSuperAdmin ? (
              <span>
                Body part{' '}
                <span
                  className="create-template-modal__required-mark"
                  aria-hidden
                >
                  *
                </span>
              </span>
            ) : null}
            <span className="bulk-upload-review-modal__action-heading">Remove</span>
          </div>
          {rows.length === 0 ? (
            <p className="bulk-upload-review-modal__empty">
              No files left. Cancel and upload again to continue.
            </p>
          ) : (
            <ul className="bulk-upload-review-modal__list">
              {displayNames.map((name, index) => (
                <li
                  key={rows[index].id}
                  className="bulk-upload-review-modal__list-item bulk-upload-review-modal__list-item--grid"
                >
                  <div className="bulk-upload-review-modal__file-cell">
                    <span className="bulk-upload-review-modal__list-index">{index + 1}.</span>
                    <span
                      className="bulk-upload-review-modal__list-name"
                      title={name}
                    >
                      {name}
                    </span>
                  </div>
                  <div className="bulk-upload-review-modal__modality-cell">
                    <VirtualizedDropdown
                      value={modalityCodeToDropdownId(modalitiesByIndex[index])}
                      onChange={isSaving ? () => {} : id => handleRowModalityChange(index, id)}
                      options={modalityDropdownOptions}
                      placeholder="Select modality"
                      showNone={false}
                      noneLabel=""
                      showSearch={true}
                      searchPlaceholder="Search..."
                      className={cn(
                        'create-template-modal__vd-trigger w-full',
                        (modalityErrors[index] || duplicateErrors[index]) &&
                          'create-template-modal__vd-trigger--invalid'
                      )}
                      containerClassName="w-full min-w-0"
                      variant="light"
                      disabled={isSaving}
                    />
                    {duplicateErrors[index] ? (
                      <p className="bulk-upload-review-modal__row-error">
                        Same name already exists or is selected twice
                      </p>
                    ) : null}
                  </div>
                  {!isSuperAdmin ? (
                    <div className="bulk-upload-review-modal__body-part-cell">
                      <BodyPartDropdown
                        modality={modalitiesByIndex[index] || undefined}
                        value={bodyPartsByIndex[index]?.id ?? null}
                        onChange={(id: number | null, label: string) => {
                          setBodyPartsByIndex(prev => {
                            const next = [...prev];
                            next[index] = { id, label: label || '' };
                            return next;
                          });
                          setBodyPartErrors(prev => {
                            const next = [...prev];
                            next[index] = false;
                            return next;
                          });
                        }}
                        placeholder="Select body part"
                        noneLabel="None"
                        showNone={false}
                        className={cn(
                          'create-template-modal__vd-trigger w-full',
                          bodyPartErrors[index] && 'create-template-modal__vd-trigger--invalid'
                        )}
                        containerClassName="w-full min-w-0"
                        variant="light"
                        disabled={isSaving || !modalitiesByIndex[index]?.trim()}
                        searchPlaceholder="Search..."
                      />
                    </div>
                  ) : null}
                  <div className="bulk-upload-review-modal__remove-cell">
                    <button
                      type="button"
                      className="bulk-upload-review-modal__remove-btn"
                      onClick={() => handleRemoveRow(index)}
                      disabled={isSaving}
                      title={`Remove ${name}`}
                      aria-label={`Remove ${name}`}
                    >
                      <Trash2
                        size={16}
                        strokeWidth={ICON_STROKE_WIDTH}
                      />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="create-template-modal__footer">
        <div className="create-template-modal__footer-actions py-1">
          <button
            type="button"
            className="create-template-modal__btn-close"
            onClick={() => hide?.()}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="create-template-modal__btn-primary inline-flex items-center justify-center gap-2"
            onClick={() => void handleSave()}
            disabled={isSaving || rows.length === 0}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Upload flow — Figma node 2:4012 */
interface UploadTemplateModalProps {
  servicesManager: ServicesManager;
  hide?: () => void;
  onJsonImportComplete: () => void | Promise<void>;
  onOpenCreateModals: (
    documents: Array<{ importedContent: string; suggestedName: string }>
  ) => void;
}

function getFileExtension(fileName: string): string | undefined {
  const parts = fileName.split('.');
  if (parts.length < 2) {
    return undefined;
  }
  return parts.pop()?.toLowerCase();
}

function collectSupportedTemplateFiles(fileList: FileList | File[] | null): File[] {
  if (!fileList?.length) {
    return [];
  }
  return Array.from(fileList).filter(file => {
    const baseName = file.name.split('/').pop() || file.name;
    if (!baseName || baseName.startsWith('.')) {
      return false;
    }
    const ext = getFileExtension(baseName);
    return !!ext && TEMPLATE_IMPORT_SUPPORTED_EXTENSIONS.includes(ext);
  });
}

type DirectoryPickerWindow = Window & {
  showDirectoryPicker?: (options?: { mode?: 'read' | 'readwrite' }) => Promise<{
    name: string;
    values?: () => AsyncIterableIterator<{
      kind: 'file' | 'directory';
      name: string;
      getFile?: () => Promise<File>;
      values?: () => AsyncIterableIterator<unknown>;
      entries?: () => AsyncIterableIterator<[string, unknown]>;
    }>;
    entries?: () => AsyncIterableIterator<
      [
        string,
        {
          kind: 'file' | 'directory';
          name: string;
          getFile?: () => Promise<File>;
          values?: () => AsyncIterableIterator<unknown>;
          entries?: () => AsyncIterableIterator<[string, unknown]>;
        },
      ]
    >;
    queryPermission?: (opts: { mode?: 'read' | 'readwrite' }) => Promise<string>;
    requestPermission?: (opts: { mode?: 'read' | 'readwrite' }) => Promise<string>;
  }>;
};

async function collectFilesFromDirectoryHandle(
  dirHandle: {
    name?: string;
    values?: () => AsyncIterableIterator<{
      kind: 'file' | 'directory';
      name: string;
      getFile?: () => Promise<File>;
      values?: () => AsyncIterableIterator<unknown>;
      entries?: () => AsyncIterableIterator<[string, unknown]>;
    }>;
    entries?: () => AsyncIterableIterator<
      [
        string,
        {
          kind: 'file' | 'directory';
          name: string;
          getFile?: () => Promise<File>;
          values?: () => AsyncIterableIterator<unknown>;
          entries?: () => AsyncIterableIterator<[string, unknown]>;
        },
      ]
    >;
    queryPermission?: (opts: { mode?: 'read' | 'readwrite' }) => Promise<string>;
    requestPermission?: (opts: { mode?: 'read' | 'readwrite' }) => Promise<string>;
  },
  state: {
    files: File[];
    totalBytes: number;
    truncated: boolean;
    truncateReason?: 'depth' | 'count' | 'size';
  } = { files: [], totalBytes: 0, truncated: false },
  pathPrefix = '',
  depth = 0
): Promise<{ files: File[]; truncated: boolean; truncateReason?: 'depth' | 'count' | 'size' }> {
  if (depth === 0 && typeof dirHandle.queryPermission === 'function') {
    let perm = await dirHandle.queryPermission({ mode: 'read' });
    if (perm !== 'granted' && typeof dirHandle.requestPermission === 'function') {
      perm = await dirHandle.requestPermission({ mode: 'read' });
    }
    if (perm !== 'granted') {
      throw new Error('Folder permission denied');
    }
  }

  if (depth > TEMPLATE_FOLDER_IMPORT_MAX_DEPTH) {
    state.truncated = true;
    state.truncateReason = state.truncateReason || 'depth';
    return state;
  }

  const handles: Array<{
    kind: 'file' | 'directory';
    name: string;
    getFile?: () => Promise<File>;
    values?: () => AsyncIterableIterator<unknown>;
    entries?: () => AsyncIterableIterator<[string, unknown]>;
  }> = [];

  if (typeof dirHandle.values === 'function') {
    for await (const entry of dirHandle.values()) {
      handles.push(entry);
    }
  } else if (typeof dirHandle.entries === 'function') {
    for await (const [, entry] of dirHandle.entries()) {
      handles.push(entry);
    }
  }

  for (const entry of handles) {
    if (state.files.length >= TEMPLATE_FOLDER_IMPORT_MAX_FILES) {
      state.truncated = true;
      state.truncateReason = 'count';
      break;
    }
    if (state.totalBytes >= TEMPLATE_FOLDER_IMPORT_MAX_TOTAL_BYTES) {
      state.truncated = true;
      state.truncateReason = 'size';
      break;
    }

    const relativePath = pathPrefix ? `${pathPrefix}/${entry.name}` : entry.name;

    if (entry.kind === 'file' && entry.getFile) {
      try {
        const file = await entry.getFile();
        const baseName = (relativePath.split('/').pop() || entry.name || '').trim();
        const ext = getFileExtension(baseName);
        if (!ext || !TEMPLATE_IMPORT_SUPPORTED_EXTENSIONS.includes(ext)) {
          continue;
        }
        if (state.totalBytes + file.size > TEMPLATE_FOLDER_IMPORT_MAX_TOTAL_BYTES) {
          state.truncated = true;
          state.truncateReason = 'size';
          break;
        }
        // Preserve folder-relative path for dedupe across same basenames in different dirs.
        try {
          Object.defineProperty(file, 'webkitRelativePath', {
            value: relativePath,
            configurable: true,
          });
        } catch {
          // Ignore if the File object is sealed.
        }
        state.files.push(file);
        state.totalBytes += file.size;
      } catch {
        // Skip unreadable entries.
      }
    } else if (entry.kind === 'directory') {
      await collectFilesFromDirectoryHandle(entry, state, relativePath, depth + 1);
      if (state.truncated && state.truncateReason === 'count') {
        break;
      }
      if (state.truncated && state.truncateReason === 'size') {
        break;
      }
    }
  }
  return state;
}

function UploadTemplateModal({
  servicesManager,
  hide,
  onJsonImportComplete,
  onOpenCreateModals,
}: UploadTemplateModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const chooseMenuRef = useRef<HTMLDivElement>(null);
  const [selectedSummary, setSelectedSummary] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [chooseMenuOpen, setChooseMenuOpen] = useState(false);
  const { templateService, uiNotificationService } = servicesManager.services;

  useEffect(() => {
    if (!chooseMenuOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (!chooseMenuRef.current?.contains(event.target as Node)) {
        setChooseMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setChooseMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [chooseMenuOpen]);

  const processSelectedFiles = async (fileList: FileList | File[] | null) => {
    let allSelected = fileList?.length ? Array.from(fileList) : [];
    if (!allSelected.length) {
      return;
    }

    const seenNames = new Set<string>();
    const dedupedSelected: File[] = [];
    let selectionDupes = 0;
    for (const file of allSelected) {
      const relative =
        (file as File & { webkitRelativePath?: string }).webkitRelativePath?.trim() || '';
      const key = (relative || file.name.split('/').pop() || file.name).trim().toLowerCase();
      if (seenNames.has(key)) {
        selectionDupes += 1;
        continue;
      }
      seenNames.add(key);
      dedupedSelected.push(file);
    }

    const supportedOnly = collectSupportedTemplateFiles(dedupedSelected);
    const unsupportedCount = dedupedSelected.length - supportedOnly.length;

    // Apply count/byte caps after extension filter so unsupported files do not consume quota.
    const capped = truncateFilesByImportCaps(
      supportedOnly,
      TEMPLATE_FOLDER_IMPORT_MAX_FILES,
      TEMPLATE_FOLDER_IMPORT_MAX_TOTAL_BYTES
    );
    if (capped.truncated) {
      const reason =
        capped.truncateReason === 'size'
          ? `Folder is large — only the first ~${Math.round(TEMPLATE_FOLDER_IMPORT_MAX_TOTAL_BYTES / (1024 * 1024))} MB will be imported.`
          : `Only the first ${TEMPLATE_FOLDER_IMPORT_MAX_FILES} files will be imported.`;
      uiNotificationService.show({
        title: capped.truncateReason === 'size' ? 'Folder too large' : 'Too many files',
        message: reason,
        type: 'info',
        duration: 5000,
      });
    }
    allSelected = capped.files;

    const files = allSelected;

    if (!files.length) {
      uiNotificationService.show({
        title: 'No Supported Files',
        message: `No supported template files found. Supported formats: ${SUPPORTED_FILE_TYPES.join(', ')}`,
        type: 'error',
        duration: 5000,
      });
      setSelectedSummary('');
      return;
    }

    setSelectedSummary(
      files.length === 1 ? files[0].name : `${files.length} files selected`
    );
    setIsParsing(true);

    let jsonSuccess = 0;
    let jsonErrors = 0;
    let failedParse = 0;
    const skippedFileNames: string[] = [];
    const parsedDocuments: Array<{ importedContent: string; suggestedName: string }> = [];

    try {
      for (const file of files) {
        const displayName = file.name.split('/').pop() || file.name;
        const fileExtension = getFileExtension(displayName);

        if (!fileExtension || !TEMPLATE_IMPORT_SUPPORTED_EXTENSIONS.includes(fileExtension)) {
          failedParse += 1;
          skippedFileNames.push(displayName);
          continue;
        }

        if (fileExtension === 'json') {
          try {
            const textContent = await file.text();
            const importedTemplates = JSON.parse(textContent);
            const templatesArray = Array.isArray(importedTemplates)
              ? importedTemplates
              : [importedTemplates];
            const result = await templateService.importTemplates(templatesArray, undefined);
            jsonSuccess += result.success;
            jsonErrors += result.errors;
          } catch {
            jsonErrors += 1;
            skippedFileNames.push(displayName);
          }
          continue;
        }

        if (
          fileExtension === 'pdf' ||
          fileExtension === 'txt' ||
          fileExtension === 'docx' ||
          fileExtension === 'doc'
        ) {
          try {
            const { htmlContent, error, isCorrupted } = await parseDocument(file);

            if (error || !htmlContent) {
              failedParse += 1;
              skippedFileNames.push(displayName);
              if (files.length === 1) {
                if (error && error.toLowerCase().includes('empty')) {
                  uiNotificationService.show({
                    title: 'Empty File',
                    message:
                      error ||
                      'File is empty or contains no readable text content. Please select a file with content.',
                    type: 'error',
                    duration: 5000,
                  });
                } else if (isCorrupted) {
                  uiNotificationService.show({
                    title: 'File Unreadable',
                    message:
                      error ||
                      'File unreadable. The file may be corrupted or in an unsupported format.',
                    type: 'error',
                    duration: 5000,
                  });
                } else {
                  const errorMessage = error || 'Failed to parse document';
                  uiNotificationService.show({
                    title: errorMessage.includes('Unable to read file')
                      ? 'Unsupported Format'
                      : 'Import Failed',
                    message: errorMessage.includes('Unable to read file')
                      ? 'Unable to read file. Try another format.'
                      : errorMessage,
                    type: 'error',
                    duration: 5000,
                  });
                }
                setSelectedSummary('');
                return;
              }
              continue;
            }

            parsedDocuments.push({
              importedContent: htmlContent,
              suggestedName: ensureMinUploadTemplateName(
                displayName.replace(/\.(pdf|txt|docx?|doc)$/i, '')
              ),
            });
          } catch (error) {
            console.error('Error parsing document:', error);
            failedParse += 1;
            skippedFileNames.push(displayName);
            if (files.length === 1) {
              uiNotificationService.show({
                title: 'File Unreadable',
                message: 'File unreadable. The file may be corrupted or in an unsupported format.',
                type: 'error',
                duration: 5000,
              });
              setSelectedSummary('');
              return;
            }
          }
        }
      }

      const skippedTotal = unsupportedCount + failedParse + selectionDupes;

      if (jsonSuccess > 0 || jsonErrors > 0) {
        await onJsonImportComplete();
        uiNotificationService.show({
          title: jsonSuccess > 0 ? 'Import Successful' : 'Import Failed',
          message: `Successfully imported ${jsonSuccess} template(s)${
            jsonErrors > 0 ? `, ${jsonErrors} failed` : ''
          }`,
          type: jsonSuccess > 0 ? 'success' : 'error',
          duration: 3000,
        });
      }

      if (parsedDocuments.length > 0) {
        if (parsedDocuments.length > 1 || skippedTotal > 0) {
          const skippedHint =
            skippedFileNames.length > 0
              ? ` Skipped: ${skippedFileNames.slice(0, 5).join(', ')}${
                  skippedFileNames.length > 5 ? '…' : ''
                }.`
              : '';
          const dupeHint =
            selectionDupes > 0
              ? ` ${selectionDupes} duplicate file name(s) ignored.`
              : '';
          uiNotificationService.show({
            title: 'Documents Ready',
            message: `${parsedDocuments.length} document(s) ready to complete.${
              skippedTotal > 0
                ? ` ${skippedTotal} file(s) skipped.${dupeHint}${skippedHint}`
                : ''
            }${
              parsedDocuments.length > TEMPLATE_BULK_UPLOAD_AUTO_CREATE_THRESHOLD
                ? ' Select a modality for each file on the next screen, then Save.'
                : ' Set modality and body part, then Save to create templates.'
            }`,
            type: 'info',
            duration: 6000,
          });
        }
        onOpenCreateModals(parsedDocuments);
        return;
      }

      if (jsonSuccess === 0 && jsonErrors === 0) {
        uiNotificationService.show({
          title: 'Import Failed',
          message:
            skippedTotal > 0
              ? 'Unable to read the selected files. Try another format.'
              : `Unable to read file. Try another format. Supported formats: ${SUPPORTED_FILE_TYPES.join(', ')}`,
          type: 'error',
          duration: 5000,
        });
        setSelectedSummary('');
        return;
      }

      hide?.();
    } finally {
      setIsParsing(false);
    }
  };

  const openMultiFilePicker = () => {
    const input = fileInputRef.current;
    if (!input) {
      return;
    }
    input.value = '';
    input.click();
  };

  const openLegacyFolderPicker = () => {
    const input = folderInputRef.current;
    if (!input) {
      return;
    }
    input.value = '';
    input.click();
  };

  const openFilePicker = (mode: 'files' | 'folder') => {
    if (isParsing) {
      return;
    }

    if (mode === 'files') {
      setChooseMenuOpen(false);
      openMultiFilePicker();
      return;
    }

    // Prefer showDirectoryPicker (no Chrome “upload all files?” dialog).
    // Call it in this click tick before setState so the user gesture is kept.
    const win = window as DirectoryPickerWindow;
    if (typeof win.showDirectoryPicker === 'function') {
      let pickerPromise: ReturnType<NonNullable<DirectoryPickerWindow['showDirectoryPicker']>>;
      try {
        pickerPromise = win.showDirectoryPicker({ mode: 'read' });
      } catch {
        setChooseMenuOpen(false);
        openLegacyFolderPicker();
        return;
      }
      setChooseMenuOpen(false);
      // Mark busy immediately so overlapping folder picks cannot start.
      setIsParsing(true);
      void pickerPromise
        .then(async dirHandle => {
          try {
            const walked = await collectFilesFromDirectoryHandle(dirHandle);
            if (walked.truncated) {
              const reason =
                walked.truncateReason === 'size'
                  ? `Folder is large — only the first ~${Math.round(TEMPLATE_FOLDER_IMPORT_MAX_TOTAL_BYTES / (1024 * 1024))} MB were imported.`
                  : walked.truncateReason === 'depth'
                    ? `Folder is deep — nested folders beyond ${TEMPLATE_FOLDER_IMPORT_MAX_DEPTH} levels were skipped.`
                    : `Folder has many files — only the first ${TEMPLATE_FOLDER_IMPORT_MAX_FILES} were imported.`;
              uiNotificationService.show({
                title: 'Folder partially imported',
                message: reason,
                type: 'info',
                duration: 6000,
              });
            }
            if (!walked.files.length) {
              uiNotificationService.show({
                title: 'Empty folder',
                message: 'No files found in the selected folder.',
                type: 'info',
                duration: 4000,
              });
              return;
            }
            await processSelectedFiles(walked.files);
          } catch (error) {
            console.warn('Folder read failed:', error);
            uiNotificationService.show({
              title: 'Folder import failed',
              message: 'Could not read the selected folder. Try Select files instead.',
              type: 'error',
              duration: 5000,
            });
          } finally {
            setIsParsing(false);
          }
        })
        .catch(error => {
          if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
            setIsParsing(false);
            return;
          }
          console.warn('Folder pick failed:', error);
          setIsParsing(false);
        });
      return;
    }

    // API unavailable: directory input (may show browser confirm once; then auto-continues).
    setChooseMenuOpen(false);
    openLegacyFolderPicker();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    void processSelectedFiles(files);
    e.target.value = '';
  };

  return (
    <div className="upload-template-modal">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".json,.doc,.docx,.txt,.pdf,application/json,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
        className="upload-template-modal__hidden-input"
        onChange={handleFileChange}
        aria-hidden
        tabIndex={-1}
      />
      <input
        ref={folderInputRef}
        type="file"
        multiple
        className="upload-template-modal__hidden-input"
        onChange={handleFileChange}
        aria-hidden
        tabIndex={-1}
        {...({ webkitdirectory: '', directory: '' } as React.InputHTMLAttributes<HTMLInputElement>)}
      />
      <div className="create-template-modal__header">
        <h2
          className="create-template-modal__title"
          id="upload-template-modal-title"
        >
          Upload template
        </h2>
      </div>

      <div className="create-template-modal__body upload-template-modal__body">
        <p className="upload-template-modal__intro">
          Select multiple files or a folder to import as report templates. For up to{' '}
          {TEMPLATE_BULK_UPLOAD_AUTO_CREATE_THRESHOLD} files, set modality and body part once — Save
          creates all templates. More than {TEMPLATE_BULK_UPLOAD_AUTO_CREATE_THRESHOLD} files open a
          review list — pick a modality for each file (and body part when required), then Save. The
          same template name cannot be uploaded or created twice.
        </p>
        <p className="upload-template-modal__format-hint">
          Accepted formats: <strong>.pdf, .doc, .docx, .txt, .json.</strong>
        </p>
        {selectedSummary ? (
          <p className="upload-template-modal__file-name">{selectedSummary}</p>
        ) : null}
      </div>

      <div className="create-template-modal__footer">
        <div className="create-template-modal__footer-actions py-1">
          <button
            type="button"
            className="create-template-modal__btn-close"
            onClick={() => hide?.()}
            disabled={isParsing}
          >
            Cancel
          </button>
          <div
            className="upload-template-modal__choose-wrap"
            ref={chooseMenuRef}
          >
            <button
              type="button"
              className="create-template-modal__btn-primary inline-flex items-center justify-center gap-2"
              onClick={() => {
                if (!isParsing) {
                  setChooseMenuOpen(open => !open);
                }
              }}
              disabled={isParsing}
              aria-haspopup="menu"
              aria-expanded={chooseMenuOpen}
              title="Select multiple files or a folder"
            >
              {isParsing ? 'Please wait…' : 'Choose files'}
            </button>
            {chooseMenuOpen ? (
              <div
                className="upload-template-modal__choose-menu"
                role="menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  className="upload-template-modal__choose-menu-item"
                  onClick={() => openFilePicker('files')}
                >
                  Select files
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="upload-template-modal__choose-menu-item"
                  onClick={() => openFilePicker('folder')}
                >
                  Select folder
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Tenant: compare facility template vs catalogue master (“View Update”, Figma). */
interface ViewTemplateUpdateModalProps {
  servicesManager: ServicesManager;
  templateId: string;
  hide?: () => void;
  onApplied?: () => void;
}

function ViewTemplateUpdateModal({
  servicesManager,
  templateId,
  hide,
  onApplied,
}: ViewTemplateUpdateModalProps) {
  const { templateService, uiNotificationService } = servicesManager.services;
  const titleId = useId();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [current, setCurrent] = useState<Template | null>(null);
  const [master, setMaster] = useState<Template | null>(null);
  const [applyBusy, setApplyBusy] = useState(false);

  useEffect(() => {
    const svc = templateService as TemplateServiceCompareApi;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setCurrent(null);
      setMaster(null);
      try {
        const result = await svc.fetchTemplateWithMaster(templateId);
        if (cancelled) {
          return;
        }
        setCurrent(result.current);
        setMaster(result.master);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load comparison.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [templateService, templateId]);

  const runMasterAction = async (action: 'UPDATE' | 'KEEP_CURRENT') => {
    if (applyBusy) {
      return;
    }
    if (action === 'UPDATE' && !master) {
      return;
    }
    const numericId = Number(templateId);
    if (!Number.isFinite(numericId) || numericId <= 0) {
      uiNotificationService.show({
        title: 'Error',
        message: 'Invalid template id.',
        type: 'error',
        duration: 5000,
      });
      return;
    }

    const svc = templateService as TemplateServiceCompareApi;
    setApplyBusy(true);
    try {
      const { message, results } = await svc.postTemplatesMasterAction([numericId], action);
      const row =
        results.find(r => r.id === numericId) ?? (results.length === 1 ? results[0] : undefined);

      if (row?.status === 'ok') {
        uiNotificationService.show({
          title: 'Success',
          message:
            message ||
            (action === 'UPDATE'
              ? 'Template updated to the latest catalogue version.'
              : 'You are keeping your current version.'),
          type: 'success',
          duration: 3000,
        });
        onApplied?.();
        hide?.();
      } else {
        uiNotificationService.show({
          title: 'Error',
          message: row?.message || message || 'The action could not be completed.',
          type: 'error',
          duration: 5000,
        });
      }
    } catch (e) {
      uiNotificationService.show({
        title: 'Error',
        message: e instanceof Error ? e.message : 'Request failed.',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setApplyBusy(false);
    }
  };

  const masterVersionLabel =
    master != null ? String(master.version ?? master.latest_version ?? '?') : null;

  return (
    <div
      className="templates-view-update-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="templates-view-update-modal__header">
        <div className="templates-view-update-modal__header-main">
          <h2
            className="create-template-modal__title templates-view-update-modal__title"
            id={titleId}
          >
            View Update
          </h2>
          <p className="templates-view-update-modal__intro">
            Super Admin published a newer catalogue version for this system template.
          </p>
        </div>
        <button
          type="button"
          className="import-library-modal__close"
          aria-label="Close"
          onClick={() => hide?.()}
          disabled={applyBusy}
        >
          <X
            className="h-4 w-4"
            strokeWidth={ICON_STROKE_WIDTH}
          />
        </button>
      </div>

      <div className="templates-view-update-modal__body">
        {loading ? (
          <p className="templates-view-update-modal__muted">Loading comparison…</p>
        ) : error && !current ? (
          <p className="templates-view-update-modal__error">{error}</p>
        ) : current ? (
          <div className="templates-view-update-modal__columns">
            <div className="templates-view-update-modal__col templates-view-update-modal__col--current">
              <h3 className="templates-view-update-modal__col-title">
                Current (your imported version)
              </h3>
              <div className="templates-view-update-modal__sections">
                <div className="templates-view-update-section">
                  <h4 className="templates-view-update-section__heading">Clinical / Technique</h4>
                  <div
                    className="templates-view-update-section__html"
                    dangerouslySetInnerHTML={{
                      __html: formatQuillHtmlForCompare(current.clinicalTechnique),
                    }}
                  />
                </div>
                <div className="templates-view-update-section">
                  <h4 className="templates-view-update-section__heading">Findings</h4>
                  <div
                    className="templates-view-update-section__html"
                    dangerouslySetInnerHTML={{
                      __html: formatQuillHtmlForCompare(current.findings),
                    }}
                  />
                </div>
                <div className="templates-view-update-section">
                  <h4 className="templates-view-update-section__heading">Impression</h4>
                  <div
                    className="templates-view-update-section__html"
                    dangerouslySetInnerHTML={{
                      __html: formatQuillHtmlForCompare(current.impression),
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="templates-view-update-modal__col templates-view-update-modal__col--update">
              <h3 className="templates-view-update-modal__col-title">
                {masterVersionLabel != null
                  ? `Update (platform v${masterVersionLabel})`
                  : 'Update (platform)'}
              </h3>
              {master ? (
                <div className="templates-view-update-modal__sections">
                  <div className="templates-view-update-section">
                    <h4 className="templates-view-update-section__heading">Clinical / Technique</h4>
                    <div
                      className="templates-view-update-section__html"
                      dangerouslySetInnerHTML={{
                        __html: formatQuillHtmlForCompare(master.clinicalTechnique),
                      }}
                    />
                  </div>
                  <div className="templates-view-update-section">
                    <h4 className="templates-view-update-section__heading">Findings</h4>
                    <div
                      className="templates-view-update-section__html"
                      dangerouslySetInnerHTML={{
                        __html: formatQuillHtmlForCompare(master.findings),
                      }}
                    />
                  </div>
                  <div className="templates-view-update-section">
                    <h4 className="templates-view-update-section__heading">Impression</h4>
                    <div
                      className="templates-view-update-section__html"
                      dangerouslySetInnerHTML={{
                        __html: formatQuillHtmlForCompare(master.impression),
                      }}
                    />
                  </div>
                </div>
              ) : (
                <p className="templates-view-update-modal__muted templates-view-update-modal__muted--column">
                  No catalogue master row was returned for this template.
                </p>
              )}
            </div>
          </div>
        ) : (
          <p className="templates-view-update-modal__error">{error || 'Unable to load comparison.'}</p>
        )}
      </div>

      <div className="create-template-modal__footer templates-view-update-modal__footer">
        <div className="create-template-modal__footer-actions">
          <button
            type="button"
            className="create-template-modal__btn-close"
            onClick={() => hide?.()}
            disabled={applyBusy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="create-template-modal__btn-close"
            onClick={() => void runMasterAction('KEEP_CURRENT')}
            disabled={applyBusy || loading || !current}
          >
            Keep current
          </button>
          <button
            type="button"
            className="create-template-modal__btn-primary"
            onClick={() => void runMasterAction('UPDATE')}
            disabled={applyBusy || !master || loading || !current}
          >
            {applyBusy ? 'Applying…' : 'Use latest'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Create/Edit Template Modal Component — Figma node 3:5985 (light shell, single scroll)
interface CreateTemplateModalProps {
  template?: Template;
  /** New template prefilled from an existing row (tenant clone). */
  duplicateFrom?: Template;
  importedContent?: string;
  suggestedName?: string;
  /** True when opened from Upload → Next flow (stronger success banner copy) */
  fromUploadFlow?: boolean;
  /** Multiple uploaded documents — Save creates one template per item with shared modality/body part. */
  batchDocuments?: Array<{ importedContent: string; suggestedName: string }>;
  /** Open template modal in read-only mode first (for View action) */
  startInViewMode?: boolean;
  /** Render as right-hand panel (no centered dialog shell) */
  embedded?: boolean;
  /** Close modal and refresh list (success / terminal paths). */
  onSave: () => void;
  /** Refresh list only — keep modal open for batch-upload retry. */
  onRefreshList?: () => void;
  servicesManager: ServicesManager;
  hide?: () => void;
}

function CreateTemplateModal({
  template,
  duplicateFrom,
  importedContent,
  suggestedName,
  fromUploadFlow = false,
  batchDocuments,
  startInViewMode = false,
  embedded = false,
  onSave,
  onRefreshList,
  servicesManager,
  hide,
}: CreateTemplateModalProps) {
  const isEditingDocumentTemplate = template?.isDocumentImport;
  const isDuplicateDocumentImport = !template && !!duplicateFrom?.isDocumentImport;
  const isDocumentImport =
    !!importedContent || !!isEditingDocumentTemplate || isDuplicateDocumentImport;
  const [isViewMode, setIsViewMode] = useState<boolean>(!!template && startInViewMode);

  const user = useAuthUser();
  const isSuperAdmin = hasRole(user, ROLES.SUPER_ADMIN);
  /** ris-radiologist: embedded drawer edit (tenant catalog), not master-templates */
  const isRadiologistTenantEdit = Boolean(!isSuperAdmin && template && embedded);
  /** System templates: view-only in drawer */
  const tenantSystemTemplateReadOnly = isRadiologistTenantEdit && template?.listType === 'System';
  /** ris-super-admin: uploaded PDF/DOC/etc. → same master-templates POST as New Template; content → findings only */
  const isMasterImportCreate = Boolean(isSuperAdmin && !template && importedContent);
  /** Super-admin editing any master-templates row (including `is_imported` / document imports): auto name, version, status */
  const isMasterEdit = isSuperAdmin && !!template;
  /** Any “new template” flow: identical catalog UI for super-admin (master API) and radiologist (tenant API). */
  const isCatalogNewCreate = !template;
  /** Tenant radiologist: centered New template — composite name row + `template_description` API field */
  const isRadiologistCatalogNewCreate = isCatalogNewCreate && !isSuperAdmin;
  /** Clamp View/Diagnosis so modality+body+view+diagnosis (+suffix) never exceeds 50 chars */
  const isRadiologistCatalogNaming =
    isRadiologistCatalogNewCreate || (isRadiologistTenantEdit && !tenantSystemTemplateReadOnly);
  /** Initial multi-file upload (2–5); stays true even if only failed rows remain for retry. */
  const startedAsBatchUpload = Boolean(
    !template && fromUploadFlow && batchDocuments && batchDocuments.length > 1
  );
  const [activeBatchDocuments, setActiveBatchDocuments] = useState<
    Array<{ importedContent: string; suggestedName: string; resolvedFileName?: string }>
  >(() => (batchDocuments ? batchDocuments.map(d => ({ ...d })) : []));
  const isBatchUploadCreate = startedAsBatchUpload && activeBatchDocuments.length > 0;

  const seedTemplate = template ?? duplicateFrom;
  const [templateName, setTemplateName] = useState(template?.name || suggestedName || '');
  const [templateDescriptionSuffix, setTemplateDescriptionSuffix] = useState(() => {
    if (!template && duplicateFrom?.template_description != null) {
      return String(duplicateFrom.template_description).replace(/\s/g, '').trim();
    }
    if (template?.template_description != null) {
      return String(template.template_description).replace(/\s/g, '').trim();
    }
    return '';
  });
  const [modality, setModality] = useState(seedTemplate?.modality || '');
  const [fullContent, setFullContent] = useState(
    importedContent ||
      (isEditingDocumentTemplate ? template?.findings : '') ||
      (isDuplicateDocumentImport ? duplicateFrom?.findings : '') ||
      ''
  );
  const [findings, setFindings] = useState(
    isEditingDocumentTemplate ? '' : template?.findings || duplicateFrom?.findings || ''
  );
  const [impression, setImpression] = useState(
    isEditingDocumentTemplate ? '' : template?.impression || duplicateFrom?.impression || ''
  );
  const [viewText, setViewText] = useState(template?.viewLabel ?? duplicateFrom?.viewLabel ?? '');
  const [diagnosisText, setDiagnosisText] = useState(() => {
    if (template?.diagnosisLabel) {
      return template.diagnosisLabel;
    }
    if (duplicateFrom?.diagnosisLabel) {
      return duplicateFrom.diagnosisLabel;
    }
    if (fromUploadFlow && suggestedName) {
      return suggestedName;
    }
    return '';
  });
  const { templateService, uiNotificationService } = servicesManager.services;

  const [selectedBodyPartId, setSelectedBodyPartId] = useState<number | null>(
    template?.anatomy_id ?? duplicateFrom?.anatomy_id ?? null
  );

  /** Embedded master edit: status matches table `is_deleted`; persisted on Save with other fields. */
  const [masterEditIsDeleted, setMasterEditIsDeleted] = useState(
    () => template?.is_deleted === true
  );

  /** Centered New Template (super-admin): initial version is v1; inactive = `is_deleted` on create. */
  const [masterCreateIsDeleted, setMasterCreateIsDeleted] = useState(false);

  /** ris-radiologist: System template → duplicate via tenant POST (same field values + “(copy)”). */
  const [duplicateSystemBusy, setDuplicateSystemBusy] = useState(false);

  type TemplateModalFieldErrors = {
    templateName?: string;
    modality?: string;
    bodyPart?: string;
    findings?: string;
    impression?: string;
    view?: string;
    diagnosis?: string;
  };

  const [fieldErrors, setFieldErrors] = useState<TemplateModalFieldErrors>({});
  const [templateNameInputFocused, setTemplateNameInputFocused] = useState(false);
  /** Custom tenant edit: header matches list `(copyN)` until user edits naming/catalog fields. */
  const [stripCopyFromRadiologistPanelHeader, setStripCopyFromRadiologistPanelHeader] =
    useState(false);
  const [headerBodyPartLabel, setHeaderBodyPartLabel] = useState(() =>
    getTemplateBodyPartHeaderLabel(seedTemplate)
  );

  useEffect(() => {
    setHeaderBodyPartLabel(getTemplateBodyPartHeaderLabel(template ?? duplicateFrom));
  }, [template?.id, duplicateFrom?.id]);

  useEffect(() => {
    setMasterEditIsDeleted(template?.is_deleted === true);
  }, [template?.id]);

  useEffect(() => {
    if (!template?.id || !isRadiologistTenantEdit || tenantSystemTemplateReadOnly) {
      return;
    }
    setTemplateDescriptionSuffix(
      template.template_description != null
        ? String(template.template_description).replace(/\s/g, '').trim()
        : ''
    );
  }, [
    template?.id,
    template?.template_description,
    isRadiologistTenantEdit,
    tenantSystemTemplateReadOnly,
  ]);

  useEffect(() => {
    setStripCopyFromRadiologistPanelHeader(false);
  }, [template?.id]);

  useEffect(() => {
    if (isViewMode) {
      setTemplateNameInputFocused(false);
    }
  }, [isViewMode]);

  const getTextLength = (content: string): number => {
    return content.replace(/<[^>]*>/g, '').length;
  };

  /** Word count from HTML (Quill) content — whitespace-separated tokens. */
  const getWordCount = (content: string): number => {
    const plain = content
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!plain) {
      return 0;
    }
    return plain.split(/\s+/).filter(Boolean).length;
  };

  const getCombinedWordCount = (): number => {
    if (isDocumentImport) {
      return getWordCount(fullContent);
    }
    return getWordCount(findings) + getWordCount(impression);
  };

  const validateCombinedContentLength = useCallback((): boolean => {
    const combinedWords = isDocumentImport
      ? getWordCount(fullContent)
      : getWordCount(findings) + getWordCount(impression);
    if (combinedWords > TEMPLATE_MAX_CONTENT_WORDS) {
      const fieldDescription = isDocumentImport
        ? 'Template content'
        : 'Combined content (Findings + Impression)';
      uiNotificationService.show({
        title: 'Content Too Large',
        message: `${fieldDescription} exceeds the maximum limit of ${TEMPLATE_MAX_CONTENT_WORDS.toLocaleString()} words. Current: ${combinedWords.toLocaleString()} words. Please reduce the content.`,
        type: 'error',
        duration: 5000,
      });
      return false;
    }
    return true;
  }, [isDocumentImport, fullContent, findings, impression, uiNotificationService]);

  const handleContentChange = (
    content: string,
    setter: (value: string) => void,
    _fieldName: string = 'Content'
  ) => {
    setter(content);
    if (_fieldName === 'Findings' || _fieldName === 'Template content') {
      setFieldErrors(prev => ({ ...prev, findings: undefined }));
    } else if (_fieldName === 'Impression') {
      setFieldErrors(prev => ({ ...prev, impression: undefined }));
    }
    setTimeout(() => {
      validateCombinedContentLength();
    }, TEMPLATE_CONTENT_VALIDATION_DELAY_MS);
  };

  useEffect(() => {
    if (!modality) {
      setSelectedBodyPartId(null);
      setHeaderBodyPartLabel('');
    }
  }, [modality]);

  const handleBodyPartChange = (bodyPartId: number | null, bodyPartName: string) => {
    markRadiologistPanelHeaderEdited();
    setFieldErrors(prev => ({ ...prev, bodyPart: undefined }));
    setSelectedBodyPartId(bodyPartId);
    if (bodyPartId == null) {
      setHeaderBodyPartLabel('');
    } else {
      setHeaderBodyPartLabel((bodyPartName || '').trim());
    }
  };

  const modalityDropdownOptions: VirtualizedDropdownOption[] = useMemo(
    () =>
      TEMPLATE_MODALITY_OPTIONS.map((o, i) => ({
        id: i + 1,
        label: o.label,
      })),
    []
  );

  const modalitySelectedId = useMemo((): number | null => {
    if (!modality) {
      return null;
    }
    const idx = TEMPLATE_MODALITY_OPTIONS.findIndex(o => o.value === modality);
    return idx >= 0 ? idx + 1 : null;
  }, [modality]);

  const handleModalityDropdownChange = (id: number | null, _label: string) => {
    setFieldErrors(prev => ({ ...prev, modality: undefined, bodyPart: undefined }));
    if (id === null) {
      setModality('');
      setSelectedBodyPartId(null);
      setHeaderBodyPartLabel('');
      markRadiologistPanelHeaderEdited();
      return;
    }
    const opt = TEMPLATE_MODALITY_OPTIONS[id - 1];
    if (opt) {
      setModality(opt.value as Template['modality']);
      markRadiologistPanelHeaderEdited();
      // New template / import: changing modality clears body part. Editing a row: keep anatomy_id + label.
      if (!template) {
        setSelectedBodyPartId(null);
        setHeaderBodyPartLabel('');
      }
    }
  };

  const handleClose = () => {
    hide?.();
  };

  const masterEditNextVersionLabel = useMemo(() => {
    if (!isMasterEdit || !template) {
      return '';
    }
    const latest =
      typeof template.latest_version === 'number'
        ? template.latest_version
        : typeof template.version === 'number'
          ? template.version
          : 0;
    return `v${latest + 1}`;
  }, [isMasterEdit, template]);

  /** Non–super-admin tenant row: show View/Diagnosis whenever list stores them; include doc-import rows for radiologist. */
  const tenantTemplateShowsViewDiagnosis =
    !!template && !isSuperAdmin && (!isDocumentImport || isRadiologistTenantEdit);

  const markRadiologistPanelHeaderEdited = useCallback(() => {
    if (isRadiologistTenantEdit && !tenantSystemTemplateReadOnly) {
      setStripCopyFromRadiologistPanelHeader(true);
    }
  }, [isRadiologistTenantEdit, tenantSystemTemplateReadOnly]);

  const validateRequiredFields = (): boolean => {
    const errors: TemplateModalFieldErrors = {};

    const usesAutoCatalogName =
      isCatalogNewCreate ||
      (isMasterEdit && embedded) ||
      (isRadiologistTenantEdit && !tenantSystemTemplateReadOnly);

    if (!usesAutoCatalogName && !templateName.trim()) {
      errors.templateName = 'Template name is required';
    }
    if (!modality.trim()) {
      errors.modality = 'Modality is required';
    }
    if (selectedBodyPartId == null) {
      errors.bodyPart = 'Body part is required';
    }
    if (isDocumentImport) {
      if (getTextLength(fullContent) === 0) {
        errors.findings = 'Template content is required';
      }
    } else if (getTextLength(findings) === 0) {
      errors.findings = 'Findings is required';
    }

    if (!isDocumentImport && getTextLength(impression) === 0) {
      errors.impression = 'Impressions is required';
    }

    if (usesAutoCatalogName && isRadiologistCatalogNaming) {
      const prefixLine = buildMasterTemplateAutoDisplayName(
        modality,
        headerBodyPartLabel,
        viewText,
        diagnosisText
      ).trim();
      const base = getRadiologistTitleBaseForLimit({
        isRadiologistCatalogNewCreate,
        isRadiologistTenantEdit,
        tenantSystemTemplateReadOnly,
        template: template ?? null,
        radiologistCatalogPrefixLine: prefixLine,
      });
      if (base.length > TEMPLATE_AUTO_NAME_MAX_LEN) {
        errors.templateName = `The fixed part of the template name exceeds ${TEMPLATE_AUTO_NAME_MAX_LEN} characters. Shorten view, diagnosis, or other fields.`;
      } else if (
        radiologistFullTitleLength(base, templateDescriptionSuffix) > TEMPLATE_AUTO_NAME_MAX_LEN
      ) {
        errors.templateName = `Template name must be at most ${TEMPLATE_AUTO_NAME_MAX_LEN} characters, including modality, body part, view, diagnosis, and description.`;
      }
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (tenantSystemTemplateReadOnly) {
      return;
    }

    if (!validateRequiredFields()) {
      return;
    }

    if (!validateCombinedContentLength()) {
      return;
    }

    try {
      if (template) {
        const templateFindings = isDocumentImport ? fullContent : findings;
        const templateImpression = isDocumentImport ? '' : impression;

        const autoCatalogName = buildMasterTemplateAutoDisplayName(
          modality,
          headerBodyPartLabel,
          viewText,
          diagnosisText
        ).trim();

        const radiologistCustomSaveName =
          isRadiologistTenantEdit && !tenantSystemTemplateReadOnly
            ? combineRadiologistTemplateTitle(
                autoCatalogName ||
                  (template.templateNameWithoutDescription
                    ? formatTemplateNameForListDisplay(template.templateNameWithoutDescription)
                    : ''),
                templateDescriptionSuffix,
                template.name
              )
            : '';

        const updatePayload = {
          name:
            isMasterEdit
              ? autoCatalogName || template.name
              : isRadiologistTenantEdit
                ? tenantSystemTemplateReadOnly
                  ? template.name
                  : radiologistCustomSaveName
                : templateName.trim() || template.name,
          modality,
          findings: templateFindings,
          impression: templateImpression,
          isDocumentImport,
          anatomy_id: selectedBodyPartId || undefined,
          viewLabel: viewText.trim(),
          diagnosisLabel: diagnosisText.trim(),
          is_deleted:
            isMasterEdit || isRadiologistTenantEdit ? masterEditIsDeleted : template.is_deleted,
          ...(isRadiologistTenantEdit && !tenantSystemTemplateReadOnly
            ? { template_description: templateDescriptionSuffix.trim() }
            : {}),
        };

        if (isSuperAdmin) {
          let modalities = getModalities();
          if (!modalities?.length) {
            await fetchAndStoreModalities();
            modalities = getModalities();
          }
          const modalityId = modalities?.find(m => m.name === modality)?.id;
          if (modalityId == null) {
            uiNotificationService.show({
              title: 'Error',
              message: 'Could not resolve modality ID. Refresh the page and try again.',
              type: 'error',
              duration: 5000,
            });
            return;
          }
          await templateService.updateTemplate(template.id, updatePayload, {
            useMasterTemplates: true,
            modalityId,
          });
        } else {
          let modalities = getModalities();
          if (!modalities?.length) {
            await fetchAndStoreModalities();
            modalities = getModalities();
          }
          const modalityId = modalities?.find(m => m.name === modality)?.id;
          if (modalityId == null) {
            uiNotificationService.show({
              title: 'Error',
              message: 'Could not resolve modality ID. Refresh the page and try again.',
              type: 'error',
              duration: 5000,
            });
            return;
          }
          await templateService.updateTemplate(template.id, updatePayload, { modalityId });
        }
        uiNotificationService.show({
          title: 'Template Updated',
          message: 'Template has been successfully updated',
          type: 'success',
          duration: 3000,
        });
      } else {
        let modalities = getModalities();
        if (!modalities?.length) {
          await fetchAndStoreModalities();
          modalities = getModalities();
        }
        const modalityId = modalities?.find(m => m.name === modality)?.id;
        if (modalityId == null) {
          uiNotificationService.show({
            title: 'Error',
            message: 'Could not resolve modality ID. Refresh the page and try again.',
            type: 'error',
            duration: 5000,
          });
          return;
        }

        const documentsToCreate = isBatchUploadCreate
          ? withResolvedBulkFileNames(activeBatchDocuments)
          : [
              {
                importedContent: isDocumentImport ? fullContent : findings,
                suggestedName: suggestedName || templateName.trim() || 'Upload',
                resolvedFileName: undefined as string | undefined,
              },
            ];

        if (isBatchUploadCreate) {
          setActiveBatchDocuments(documentsToCreate);
        }

        let createdCount = 0;
        let failedCount = 0;
        let skippedDuplicates = 0;
        const failedDocs: typeof documentsToCreate = [];
        const existingKeys =
          fromUploadFlow || isBatchUploadCreate
            ? await fetchExistingUploadDuplicateKeys(
                templateService as {
                  fetchTemplatesFromApi: (options?: {
                    page?: number;
                    limit?: number;
                    templatesListBaseUrl?: string;
                  }) => Promise<{ templates: Template[] }>;
                },
                isSuperAdmin
              )
            : new Set<string>();
        const seenBatchKeys = new Set<string>(existingKeys);

        for (let i = 0; i < documentsToCreate.length; i += 1) {
          const doc = documentsToCreate[i];
          const fileName =
            (doc.resolvedFileName ||
              doc.suggestedName ||
              suggestedName ||
              `Upload-${i + 1}`
            ).trim() || `Upload-${i + 1}`;
          const templateFindings = doc.importedContent;

          // Batch upload: same as bulk — unique file basename is the persisted identity
          // (not Template-1 / Template-2). Single-file upload keeps editable diagnosis.
          const docDiagnosis = isBatchUploadCreate ? fileName : diagnosisText.trim();

          const namePrefix = buildMasterTemplateAutoDisplayName(
            modality,
            headerBodyPartLabel,
            viewText,
            docDiagnosis
          ).trim();

          let newTemplateName: string;
          if (isBatchUploadCreate) {
            newTemplateName = resolveUploadPersistedName({
              isSuperAdmin,
              fileName,
              modality,
              bodyPartLabel: headerBodyPartLabel,
              view: viewText,
              descriptionSuffix: isRadiologistCatalogNewCreate
                ? templateDescriptionSuffix
                : '',
            });
          } else if (isRadiologistCatalogNewCreate) {
            newTemplateName = combineRadiologistTemplateTitle(
              namePrefix,
              templateDescriptionSuffix,
              MASTER_TEMPLATE_NEW_DISPLAY_NAME
            );
          } else {
            newTemplateName = namePrefix || MASTER_TEMPLATE_NEW_DISPLAY_NAME;
          }

          if (duplicateFrom && !template && !isBatchUploadCreate) {
            const copySuffix = ' (copy)';
            if (!newTemplateName.endsWith(copySuffix)) {
              newTemplateName = `${newTemplateName}${copySuffix}`;
            }
          }

          if (fromUploadFlow || isBatchUploadCreate) {
            const dupKey = uploadDuplicateKey(newTemplateName, modality);
            if (seenBatchKeys.has(dupKey)) {
              skippedDuplicates += 1;
              continue;
            }
          }

          const catalogCreatePayload: Omit<Template, 'id' | 'createdAt' | 'updatedAt'> = {
            name: newTemplateName,
            modality,
            findings: templateFindings,
            impression: isDocumentImport ? '' : impression,
            isDocumentImport,
            viewLabel: viewText.trim(),
            diagnosisLabel: isBatchUploadCreate && isSuperAdmin ? '' : docDiagnosis,
            ...(selectedBodyPartId != null ? { anatomy_id: selectedBodyPartId } : {}),
            status: masterCreateIsDeleted ? 'Inactive' : 'Active',
            is_deleted: masterCreateIsDeleted || undefined,
            ...(isRadiologistCatalogNewCreate
              ? { template_description: templateDescriptionSuffix.trim() }
              : {}),
          };

          try {
            if (isSuperAdmin) {
              await templateService.createTemplate(catalogCreatePayload, {
                useMasterTemplates: true,
                modalityId,
              });
            } else {
              await templateService.createTemplate(catalogCreatePayload, { modalityId });
            }
            if (fromUploadFlow || isBatchUploadCreate) {
              seenBatchKeys.add(uploadDuplicateKey(newTemplateName, modality));
            }
            createdCount += 1;
          } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            if (/already exists|duplicate|unique/i.test(message)) {
              skippedDuplicates += 1;
              if (fromUploadFlow || isBatchUploadCreate) {
                seenBatchKeys.add(uploadDuplicateKey(newTemplateName, modality));
              }
            } else {
              console.error(`Failed to create template for ${fileName}:`, error);
              failedCount += 1;
              if (isBatchUploadCreate) {
                failedDocs.push(doc);
              }
            }
          }
        }

        // Prefer retaining API failures for retry — even when createdCount is 0 and some were duplicates.
        if (isBatchUploadCreate && failedCount > 0 && failedDocs.length > 0) {
          setActiveBatchDocuments(failedDocs);
          uiNotificationService.show({
            title: createdCount > 0 ? 'Partial import' : 'Import Failed',
            message: `${
              createdCount > 0 ? `Created ${createdCount}, ` : ''
            }${failedCount} failed — remaining file(s) stay open so you can retry.${
              skippedDuplicates > 0 ? ` ${skippedDuplicates} duplicate(s) skipped.` : ''
            }`,
            type: createdCount > 0 ? 'info' : 'error',
            duration: 7000,
          });
          if (
            batchUploadShouldRefreshWithoutClose({
              failedCount,
              failedDocsLength: failedDocs.length,
              createdCount,
              skippedDuplicates,
            })
          ) {
            onRefreshList?.();
          }
          return;
        }

        if (createdCount === 0) {
          uiNotificationService.show({
            title: skippedDuplicates > 0 ? 'Duplicates skipped' : 'Error',
            message:
              skippedDuplicates > 0
                ? `All selected file(s) were skipped because a template with the same name already exists.`
                : 'Failed to create templates. Please try again.',
            type: skippedDuplicates > 0 ? 'info' : 'error',
            duration: 5000,
          });
          // Close only when there is nothing left to retry (no API failures).
          if (skippedDuplicates > 0 && failedCount === 0) {
            onSave();
            hide?.();
          }
          return;
        }

        uiNotificationService.show({
          title: createdCount > 1 ? 'Templates Created' : 'Template Created',
          message:
            createdCount > 1
              ? `Successfully created ${createdCount} template(s)${
                  skippedDuplicates > 0 ? `, ${skippedDuplicates} duplicate(s) skipped` : ''
                }`
              : skippedDuplicates > 0
                ? `Template created. ${skippedDuplicates} duplicate(s) skipped.`
                : 'Template has been successfully created',
          type: 'success',
          duration: 3000,
        });
      }
      onSave();
      hide?.();
    } catch (error) {
      console.error('Error saving template:', error);

      let errorMessage = 'Failed to save template';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      uiNotificationService.show({
        title: 'Error',
        message: errorMessage,
        type: 'error',
        duration: 5000,
      });
    }
  };

  const handleDuplicateSystemToMyTemplates = useCallback(async () => {
    if (!template?.id || !tenantSystemTemplateReadOnly) {
      return;
    }
    if (!validateCombinedContentLength()) {
      return;
    }
    setDuplicateSystemBusy(true);
    try {
      const anatomyIdForCreate = selectedBodyPartId ?? template.anatomy_id;
      if (anatomyIdForCreate == null) {
        uiNotificationService.show({
          title: 'Error',
          message: 'Body part is required to duplicate this template.',
          type: 'error',
          duration: 5000,
        });
        return;
      }

      const templateFindings = isDocumentImport ? fullContent : findings;

      let modalities = getModalities();
      if (!modalities?.length) {
        await fetchAndStoreModalities();
        modalities = getModalities();
      }
      const modalityId = modalities?.find(m => m.name === modality)?.id;
      if (modalityId == null) {
        uiNotificationService.show({
          title: 'Error',
          message: 'Could not resolve modality ID. Refresh the page and try again.',
          type: 'error',
          duration: 5000,
        });
        return;
      }

      let newTemplateName =
        buildMasterTemplateAutoDisplayName(
          modality,
          headerBodyPartLabel,
          viewText,
          diagnosisText
        ).trim() || MASTER_TEMPLATE_NEW_DISPLAY_NAME;
      const copySuffix = ' (copy)';
      if (!newTemplateName.endsWith(copySuffix)) {
        newTemplateName = `${newTemplateName}${copySuffix}`;
      }

      const catalogCreatePayload: Omit<Template, 'id' | 'createdAt' | 'updatedAt'> = {
        name: newTemplateName,
        modality,
        findings: templateFindings,
        impression: isDocumentImport ? '' : impression,
        isDocumentImport: !!template.isDocumentImport,
        anatomy_id: anatomyIdForCreate,
        viewLabel: viewText.trim(),
        diagnosisLabel: diagnosisText.trim(),
        status: 'Active',
        is_deleted: false,
        access_level: template.access_level,
        facility_id: template.facility_id,
      };

      await templateService.createTemplate(catalogCreatePayload, { modalityId });

      uiNotificationService.show({
        title: 'Template created',
        message: 'A custom copy was added to your templates.',
        type: 'success',
        duration: 3000,
      });
      onSave();
      hide?.();
    } catch (error) {
      console.error('Duplicate system template failed:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'Failed to duplicate template';
      uiNotificationService.show({
        title: 'Error',
        message: errorMessage,
        type: 'error',
        duration: 5000,
      });
    } finally {
      setDuplicateSystemBusy(false);
    }
  }, [
    template,
    tenantSystemTemplateReadOnly,
    isDocumentImport,
    fullContent,
    findings,
    impression,
    modality,
    headerBodyPartLabel,
    viewText,
    diagnosisText,
    selectedBodyPartId,
    templateService,
    uiNotificationService,
    onSave,
    hide,
    validateCombinedContentLength,
  ]);

  /** Tenant embedded: System = API/static title; Custom = live name from modality … diagnosis (same as super-admin catalog edit). */
  const radiologistEmbeddedDisplayName = useMemo(() => {
    if (!template || !isRadiologistTenantEdit) {
      return '';
    }

    const apiStaticPretty = (): string => {
      const raw = String(
        template.name ?? (template as unknown as { template_name?: string }).template_name ?? ''
      ).trim();
      if (!raw) {
        return '—';
      }
      const pretty = formatTemplateNameForListDisplay(raw);
      return pretty || raw;
    };

    if (tenantSystemTemplateReadOnly) {
      return apiStaticPretty();
    }

    const built = buildMasterTemplateAutoDisplayName(
      modality,
      headerBodyPartLabel,
      viewText,
      diagnosisText
    ).trim();
    if (built) {
      return built;
    }
    return apiStaticPretty();
  }, [
    template,
    isRadiologistTenantEdit,
    tenantSystemTemplateReadOnly,
    modality,
    headerBodyPartLabel,
    viewText,
    diagnosisText,
  ]);

  /** Auto-built template title from modality → body part → view → diagnosis (catalog new create + embedded master edit). */
  const masterAutoTemplateNameLine = useMemo(() => {
    if (template) {
      if (!(isMasterEdit && embedded)) {
        return '';
      }
      const built = buildMasterTemplateAutoDisplayName(
        modality,
        headerBodyPartLabel,
        viewText,
        diagnosisText
      );
      const trimmed = built.trim();
      if (trimmed) {
        return trimmed;
      }
      return template?.name?.trim() || '';
    }
    const built = buildMasterTemplateAutoDisplayName(
      modality,
      headerBodyPartLabel,
      viewText,
      diagnosisText
    );
    const trimmed = built.trim();
    if (trimmed) {
      return trimmed;
    }
    return MASTER_TEMPLATE_NEW_DISPLAY_NAME;
  }, [template, isMasterEdit, embedded, modality, headerBodyPartLabel, viewText, diagnosisText]);

  const embeddedReadonlyNameInputValue =
    isMasterEdit && embedded
      ? masterAutoTemplateNameLine
      : isRadiologistTenantEdit
        ? radiologistEmbeddedDisplayName
        : masterAutoTemplateNameLine;

  const radiologistCatalogPrefixLine = useMemo(
    () =>
      buildMasterTemplateAutoDisplayName(
        modality,
        headerBodyPartLabel,
        viewText,
        diagnosisText
      ).trim(),
    [modality, headerBodyPartLabel, viewText, diagnosisText]
  );

  const radiologistTitleBaseForLimit = useMemo(
    () =>
      getRadiologistTitleBaseForLimit({
        isRadiologistCatalogNewCreate,
        isRadiologistTenantEdit,
        tenantSystemTemplateReadOnly,
        template: template ?? null,
        radiologistCatalogPrefixLine,
      }),
    [
      isRadiologistCatalogNewCreate,
      isRadiologistTenantEdit,
      tenantSystemTemplateReadOnly,
      template,
      radiologistCatalogPrefixLine,
    ]
  );

  const radiologistMaxSuffixChars = useMemo(
    () => computeRadiologistMaxSuffixChars(radiologistTitleBaseForLimit),
    [radiologistTitleBaseForLimit]
  );

  const radiologistCompositeTitleCharCount = useMemo(
    () =>
      radiologistFullTitleLength(radiologistTitleBaseForLimit, templateDescriptionSuffix),
    [radiologistTitleBaseForLimit, templateDescriptionSuffix]
  );

  /** When modality/body prefix grows, shrink allowed suffix so total stays ≤ 50 (spaces never allowed). */
  useEffect(() => {
    if (!isRadiologistCatalogNaming) {
      return;
    }
    setTemplateDescriptionSuffix(prev => {
      const clean = prev.replace(/\s/g, '');
      const max = computeRadiologistMaxSuffixChars(radiologistTitleBaseForLimit);
      if (clean.length <= max) {
        return clean;
      }
      return clean.slice(0, max);
    });
  }, [isRadiologistCatalogNaming, radiologistTitleBaseForLimit]);

  /** View/Diagnosis may not push modality+body+view+diagnosis beyond 50 chars (trim diagnosis, then view). */
  useEffect(() => {
    if (!isRadiologistCatalogNaming) {
      return;
    }
    const c = clampViewAndDiagnosisToCatalogPrefixMax(
      modality,
      headerBodyPartLabel,
      viewText,
      diagnosisText,
      TEMPLATE_AUTO_NAME_MAX_LEN
    );
    if (c.view !== viewText || c.diagnosis !== diagnosisText) {
      setViewText(c.view);
      setDiagnosisText(c.diagnosis);
    }
  }, [
    isRadiologistCatalogNaming,
    modality,
    headerBodyPartLabel,
    viewText,
    diagnosisText,
  ]);

  /** Composite prefix cell: live build, else API `template_name_without_description`, else Untitled (new only). */
  const radiologistCompositePrefixDisplay = useMemo(() => {
    if (radiologistCatalogPrefixLine) {
      return radiologistCatalogPrefixLine;
    }
    if (
      template?.templateNameWithoutDescription != null &&
      String(template.templateNameWithoutDescription).trim()
    ) {
      return formatTemplateNameForListDisplay(String(template.templateNameWithoutDescription));
    }
    if (!template) {
      return MASTER_TEMPLATE_NEW_DISPLAY_NAME;
    }
    return MASTER_TEMPLATE_NEW_DISPLAY_NAME;
  }, [radiologistCatalogPrefixLine, template]);

  const radiologistEditCompositeFullTitle = useMemo(() => {
    if (!template || !isRadiologistTenantEdit || tenantSystemTemplateReadOnly) {
      return '';
    }
    const prefixForCombine =
      radiologistCatalogPrefixLine ||
      (template.templateNameWithoutDescription
        ? formatTemplateNameForListDisplay(template.templateNameWithoutDescription)
        : '');
    return combineRadiologistTemplateTitle(
      prefixForCombine,
      templateDescriptionSuffix,
      formatTemplateNameForListDisplay(template.name)
    );
  }, [
    template,
    isRadiologistTenantEdit,
    tenantSystemTemplateReadOnly,
    radiologistCatalogPrefixLine,
    templateDescriptionSuffix,
  ]);

  const radiologistNewCreateFullTitle = useMemo(
    () =>
      combineRadiologistTemplateTitle(
        radiologistCatalogPrefixLine,
        templateDescriptionSuffix,
        MASTER_TEMPLATE_NEW_DISPLAY_NAME
      ),
    [radiologistCatalogPrefixLine, templateDescriptionSuffix]
  );

  /** Right-panel header for ris-radiologist custom templates: list title + copy label until user edits. */
  const radiologistCustomPanelHeaderTitle = useMemo(() => {
    if (!template || !isRadiologistTenantEdit || tenantSystemTemplateReadOnly) {
      return '';
    }
    if (stripCopyFromRadiologistPanelHeader) {
      return radiologistEditCompositeFullTitle;
    }
    return templateRowDisplayName(template);
  }, [
    template,
    isRadiologistTenantEdit,
    tenantSystemTemplateReadOnly,
    stripCopyFromRadiologistPanelHeader,
    radiologistEditCompositeFullTitle,
  ]);

  const modalHeadingDisplay = template
    ? isMasterEdit && embedded
      ? masterAutoTemplateNameLine || template.name
      : isRadiologistTenantEdit
        ? tenantSystemTemplateReadOnly
          ? radiologistEmbeddedDisplayName || String(template.name ?? '')
          : radiologistCustomPanelHeaderTitle
        : truncateTemplateNameForDisplay(templateName.trim() || template.name)
    : isRadiologistCatalogNewCreate
      ? radiologistNewCreateFullTitle
      : masterAutoTemplateNameLine;
  const modalHeadingTooltip = template
    ? isMasterEdit && embedded
      ? masterAutoTemplateNameLine || template.name
      : isRadiologistTenantEdit
        ? tenantSystemTemplateReadOnly
          ? radiologistEmbeddedDisplayName || template.name
          : radiologistCustomPanelHeaderTitle
        : templateName.trim() || template.name
    : isRadiologistCatalogNewCreate
      ? radiologistNewCreateFullTitle
      : masterAutoTemplateNameLine;
  const primaryLabel = template ? (isViewMode ? 'Edit Template' : 'Save') : 'Save';

  /** View + Diagnosis for catalog new create and tenant/master edits that use the API shape. */
  const showViewDiagnosisFields =
    isCatalogNewCreate ||
    (isSuperAdmin && !!template) ||
    (isSuperAdmin && !template && !!importedContent) ||
    tenantTemplateShowsViewDiagnosis;
  const headerCategoryLabel = template ? headerBodyPartLabel : '';

  const handlePrimaryAction = () => {
    if (template && isViewMode) {
      setIsViewMode(false);
      return;
    }
    handleSave();
  };

  const useReadonlyAutoTemplateName =
    isCatalogNewCreate || (isMasterEdit && embedded) || isRadiologistTenantEdit;

  /** Custom tenant templates: editable fields. System: read-only. */
  const tenantDrawerFieldsDisabled = isViewMode || tenantSystemTemplateReadOnly;

  const radiologistEmbeddedVersionLabel = useMemo(() => {
    if (!template || !isRadiologistTenantEdit) {
      return '';
    }
    const v = template.version ?? 1;
    return `v${v}`;
  }, [template, isRadiologistTenantEdit]);

  return (
    <div
      className={cn(
        'create-template-modal',
        embedded && 'create-template-modal--embedded',
        isViewMode && 'create-template-modal--view',
        useReadonlyAutoTemplateName && 'create-template-modal--live-name'
      )}
    >
      <div
        className={cn(
          'create-template-modal__header',
          embedded && 'create-template-modal__header--embedded'
        )}
      >
        <div className="create-template-modal__header-main">
          <h2
            className="create-template-modal__title"
            title={modalHeadingTooltip || undefined}
            aria-label={modalHeadingTooltip ? `Template: ${modalHeadingTooltip}` : undefined}
          >
            {modalHeadingDisplay}
          </h2>
          {template && modality && !isMasterEdit && !isRadiologistTenantEdit && (
            <span className="create-template-modal__modality-badge">{modality}</span>
          )}
          {template && headerCategoryLabel && !isMasterEdit && !isRadiologistTenantEdit && (
            <span className="create-template-modal__modality-badge">{headerCategoryLabel}</span>
          )}
        </div>
        {embedded && (
          <button
            type="button"
            className="create-template-modal__panel-close"
            onClick={handleClose}
            aria-label="Close panel"
          >
            <X
              className="h-4 w-4"
              strokeWidth={ICON_STROKE_WIDTH}
              aria-hidden
            />
          </button>
        )}
      </div>

      <div className="create-template-modal__body">
        {importedContent && (
          <div className="create-template-modal__import-banner">
            {fromUploadFlow
              ? isBatchUploadCreate
                ? `You selected ${activeBatchDocuments.length} file(s). Set modality and body part below — Save creates one template per file using each file’s name (same as bulk upload).`
                : isMasterImportCreate || isCatalogNewCreate
                  ? 'Your file was uploaded and parsed successfully. Set modality, body part, view, and diagnosis below — the template name builds automatically — then click Save.'
                  : 'Your file was uploaded and parsed successfully. Complete the template details below, then click Create Template.'
              : isCatalogNewCreate
                ? 'Document content imported successfully. Set modality, body part, view, and diagnosis — the template name builds automatically — then review the content below.'
                : 'Document content imported successfully. Enter a template name and select modality and body part, then review the content below.'}
          </div>
        )}

        <div className="create-template-modal__row2">
          <div className="create-template-modal__field">
            <span className="create-template-modal__label">
              Modality{' '}
              <span
                className="create-template-modal__required-mark"
                aria-hidden
              >
                *
              </span>
            </span>
            <VirtualizedDropdown
              value={modalitySelectedId}
              onChange={tenantDrawerFieldsDisabled ? () => {} : handleModalityDropdownChange}
              options={modalityDropdownOptions}
              placeholder="Select modality"
              showNone={false}
              noneLabel=""
              showSearch={true}
              searchPlaceholder="Search..."
              className={cn(
                'create-template-modal__vd-trigger w-full',
                fieldErrors.modality && 'create-template-modal__vd-trigger--invalid'
              )}
              containerClassName="w-full min-w-0"
              variant="light"
              disabled={tenantDrawerFieldsDisabled}
            />
            {fieldErrors.modality && (
              <p
                className="create-template-modal__field-error"
                role="alert"
              >
                {fieldErrors.modality}
              </p>
            )}
          </div>
          <div className="create-template-modal__field">
            <span className="create-template-modal__label">
              Body part{' '}
              <span
                className="create-template-modal__required-mark"
                aria-hidden
              >
                *
              </span>
            </span>
            <BodyPartDropdown
              modality={modality || undefined}
              value={selectedBodyPartId}
              onChange={tenantDrawerFieldsDisabled ? () => {} : handleBodyPartChange}
              placeholder="Select body part"
              noneLabel="None"
              showNone={false}
              className={cn(
                'create-template-modal__vd-trigger w-full',
                fieldErrors.bodyPart && 'create-template-modal__vd-trigger--invalid'
              )}
              containerClassName="w-full min-w-0"
              variant="light"
              disabled={tenantDrawerFieldsDisabled || !modality}
              searchPlaceholder="Search..."
            />
            {fieldErrors.bodyPart && (
              <p
                className="create-template-modal__field-error"
                role="alert"
              >
                {fieldErrors.bodyPart}
              </p>
            )}
          </div>
        </div>

        {showViewDiagnosisFields && (
          <div className="create-template-modal__row2">
            <div className="create-template-modal__field">
              <label
                className="create-template-modal__label"
                htmlFor="ris-tpl-view"
              >
                View
              </label>
              <input
                id="ris-tpl-view"
                type="text"
                value={viewText}
                onChange={e => {
                  markRadiologistPanelHeaderEdited();
                  const next = e.target.value;
                  if (isRadiologistCatalogNaming) {
                    const c = clampViewAndDiagnosisToCatalogPrefixMax(
                      modality,
                      headerBodyPartLabel,
                      next,
                      diagnosisText,
                      TEMPLATE_AUTO_NAME_MAX_LEN
                    );
                    setViewText(c.view);
                    setDiagnosisText(c.diagnosis);
                  } else {
                    setViewText(next);
                  }
                  setFieldErrors(prev => ({ ...prev, view: undefined }));
                }}
                placeholder="e.g. PA, lateral"
                disabled={tenantDrawerFieldsDisabled}
                autoComplete="off"
                aria-invalid={!!fieldErrors.view}
                className={cn(
                  'create-template-modal__input',
                  fieldErrors.view && 'create-template-modal__input--invalid'
                )}
              />
              {fieldErrors.view && (
                <p
                  className="create-template-modal__field-error"
                  role="alert"
                >
                  {fieldErrors.view}
                </p>
              )}
            </div>
            <div className="create-template-modal__field">
              <label
                className="create-template-modal__label"
                htmlFor="ris-tpl-dx"
              >
                Diagnosis
              </label>
              <input
                id="ris-tpl-dx"
                type="text"
                value={diagnosisText}
                onChange={e => {
                  markRadiologistPanelHeaderEdited();
                  const next = e.target.value;
                  if (isRadiologistCatalogNaming) {
                    const c = clampViewAndDiagnosisToCatalogPrefixMax(
                      modality,
                      headerBodyPartLabel,
                      viewText,
                      next,
                      TEMPLATE_AUTO_NAME_MAX_LEN
                    );
                    setViewText(c.view);
                    setDiagnosisText(c.diagnosis);
                  } else {
                    setDiagnosisText(next);
                  }
                  setFieldErrors(prev => ({ ...prev, diagnosis: undefined }));
                }}
                placeholder="e.g. Stroke, tumor, trauma"
                disabled={tenantDrawerFieldsDisabled}
                autoComplete="off"
                aria-invalid={!!fieldErrors.diagnosis}
                className={cn(
                  'create-template-modal__input',
                  fieldErrors.diagnosis && 'create-template-modal__input--invalid'
                )}
              />
              {fieldErrors.diagnosis && (
                <p
                  className="create-template-modal__field-error"
                  role="alert"
                >
                  {fieldErrors.diagnosis}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="create-template-modal__field">
          <div className="create-template-modal__tpl-name-label-row">
            <label
              className="create-template-modal__label"
              htmlFor={
                isViewMode
                  ? 'create-template-name'
                  : useReadonlyAutoTemplateName
                    ? isRadiologistCatalogNewCreate ||
                      (isRadiologistTenantEdit && !tenantSystemTemplateReadOnly)
                      ? 'ris-tpl-name-suffix'
                      : 'ris-tpl-auto-name'
                    : 'create-template-name'
              }
            >
              Template name{' '}
              {!useReadonlyAutoTemplateName && (
                <span
                  className="create-template-modal__required-mark"
                  aria-hidden
                >
                  *
                </span>
              )}
            </label>
            {useReadonlyAutoTemplateName && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="templates-th-info create-template-modal__tpl-name-info"
                    aria-label="About template name"
                  >
                    <Icons.Info className="templates-th-info-icon" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="max-w-xs text-xs"
                >
                  {isRadiologistCatalogNewCreate ||
                  (isRadiologistTenantEdit && !tenantSystemTemplateReadOnly)
                    ? 'Left: fixed prefix (live fields; API template_name_without_description when unchanged). Right: template_description.'
                    : isRadiologistTenantEdit
                      ? tenantSystemTemplateReadOnly
                        ? 'System templates use the catalog title from the platform library.'
                        : 'Generated from modality, body part, view, and diagnosis. The server stores the final title when you save.'
                      : 'Generated from modality, body part, view, and diagnosis. The server stores the final title when you save.'}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          {isViewMode ? (
            <div
              id="create-template-name"
              role="textbox"
              aria-readonly="true"
              aria-label={
                isMasterEdit && embedded
                  ? `Template name: ${masterAutoTemplateNameLine || template?.name || ''}`
                  : isRadiologistTenantEdit
                    ? tenantSystemTemplateReadOnly
                      ? `Template name: ${radiologistEmbeddedDisplayName || template?.name || ''}`
                      : `Template name: ${template ? templateRowDisplayName(template) : ''}`
                    : templateName.trim()
                      ? `Template name: ${templateName.trim()}`
                      : 'Template name'
              }
              aria-invalid={!!fieldErrors.templateName}
              title={
                isMasterEdit && embedded
                  ? masterAutoTemplateNameLine || template?.name
                  : isRadiologistTenantEdit
                    ? tenantSystemTemplateReadOnly
                      ? radiologistEmbeddedDisplayName || template?.name
                      : template
                        ? templateRowDisplayName(template)
                        : undefined
                    : templateName.trim() || undefined
              }
              className={cn(
                'create-template-modal__input',
                'create-template-modal__input--template-name-view',
                fieldErrors.templateName && 'create-template-modal__input--invalid'
              )}
            >
              {isMasterEdit && embedded
                ? masterAutoTemplateNameLine || template?.name || ''
                : isRadiologistTenantEdit
                  ? tenantSystemTemplateReadOnly
                    ? radiologistEmbeddedDisplayName || template?.name || ''
                    : template
                      ? templateRowDisplayName(template)
                      : ''
                  : templateName.trim()
                    ? truncateTemplateNameForDisplay(templateName.trim())
                    : ''}
            </div>
          ) : useReadonlyAutoTemplateName ? (
            isRadiologistCatalogNewCreate ||
            (isRadiologistTenantEdit && !tenantSystemTemplateReadOnly) ? (
              <div
                className={cn(
                  'create-template-modal__tpl-name-composite',
                  fieldErrors.templateName && 'create-template-modal__tpl-name-composite--invalid'
                )}
              >
                <span
                  className="create-template-modal__tpl-name-prefix"
                  title={radiologistCompositePrefixDisplay}
                >
                  <span className="create-template-modal__tpl-name-prefix-inner">
                    {radiologistCompositePrefixDisplay}
                  </span>
                </span>
                <div className="create-template-modal__tpl-name-suffix-wrap">
                  <input
                    id="ris-tpl-name-suffix"
                    type="text"
                    value={templateDescriptionSuffix}
                    onChange={e => {
                      markRadiologistPanelHeaderEdited();
                      const noSpace = e.target.value.replace(/\s/g, '');
                      const max = computeRadiologistMaxSuffixChars(radiologistTitleBaseForLimit);
                      setTemplateDescriptionSuffix(noSpace.slice(0, max));
                      setFieldErrors(prev => ({ ...prev, templateName: undefined }));
                    }}
                    className="create-template-modal__tpl-name-suffix-input"
                    aria-invalid={!!fieldErrors.templateName}
                    aria-describedby="ris-tpl-name-count"
                    aria-label="Template name, text after the fixed prefix"
                    placeholder=""
                    autoComplete="off"
                  />
                  <span
                    id="ris-tpl-name-count"
                    className="create-template-modal__tpl-name-suffix-count"
                    aria-live="polite"
                  >
                    {radiologistCompositeTitleCharCount}/{TEMPLATE_AUTO_NAME_MAX_LEN}
                  </span>
                </div>
              </div>
            ) : (
              <div className="relative">
                <input
                  id="ris-tpl-auto-name"
                  type="text"
                  readOnly
                  aria-readonly="true"
                  value={embeddedReadonlyNameInputValue}
                  title={embeddedReadonlyNameInputValue}
                  className={cn(
                    'create-template-modal__input',
                    'create-template-modal__input--template-name',
                    'pr-16',
                    fieldErrors.templateName && 'create-template-modal__input--invalid'
                  )}
                  aria-describedby="ris-tpl-auto-name-count"
                />
                <span
                  id="ris-tpl-auto-name-count"
                  className="create-template-modal__char-count"
                  aria-live="polite"
                >
                  {embeddedReadonlyNameInputValue.length}/{TEMPLATE_AUTO_NAME_MAX_LEN}
                </span>
              </div>
            )
          ) : (
            <input
              id="create-template-name"
              type="text"
              value={
                templateNameInputFocused || templateName.length <= TEMPLATE_NAME_DISPLAY_MAX
                  ? templateName
                  : truncateTemplateNameForDisplay(templateName)
              }
              onChange={e => {
                setTemplateName(e.target.value);
                setFieldErrors(prev => ({ ...prev, templateName: undefined }));
              }}
              onFocus={() => setTemplateNameInputFocused(true)}
              onBlur={() => setTemplateNameInputFocused(false)}
              placeholder="Enter Name"
              title={templateName ? templateName : undefined}
              className={cn(
                'create-template-modal__input',
                'create-template-modal__input--template-name',
                fieldErrors.templateName && 'create-template-modal__input--invalid'
              )}
              autoComplete="off"
              autoFocus={!isViewMode}
              aria-invalid={!!fieldErrors.templateName}
              aria-describedby={fieldErrors.templateName ? 'create-template-name-error' : undefined}
            />
          )}
          {fieldErrors.templateName && (
            <p
              id="create-template-name-error"
              className="create-template-modal__field-error"
              role="alert"
            >
              {fieldErrors.templateName}
            </p>
          )}
        </div>

        {isDocumentImport ? (
          <div className="create-template-modal__field">
            <div className="create-template-modal__label">
              Template content{' '}
              <span
                className="create-template-modal__required-mark"
                aria-hidden
              >
                *
              </span>
            </div>
            <div className="create-template-quill-wrap">
              <div
                className={cn(
                  'create-template-quill create-template-quill--full',
                  fieldErrors.findings && 'create-template-quill--has-error'
                )}
              >
                <ReactQuill
                  theme="snow"
                  value={fullContent}
                  readOnly={tenantDrawerFieldsDisabled}
                  onChange={content =>
                    handleContentChange(content, setFullContent, 'Template content')
                  }
                  modules={TEMPLATE_QUILL_MODULES}
                  formats={TEMPLATE_QUILL_FORMATS}
                  placeholder="Edit your imported document content here..."
                />
              </div>
              <div
                className={cn(
                  'create-template-modal__count create-template-modal__count--overlay',
                  getWordCount(fullContent) > TEMPLATE_MAX_CONTENT_WORDS &&
                    'create-template-modal__count--over-limit'
                )}
                aria-live="polite"
              >
                {getWordCount(fullContent).toLocaleString()} /{' '}
                {TEMPLATE_MAX_CONTENT_WORDS.toLocaleString()} words
                {getWordCount(fullContent) > TEMPLATE_MAX_CONTENT_WORDS && (
                  <span className="create-template-modal__count-exceeds"> (Exceeds limit)</span>
                )}
              </div>
            </div>
            {fieldErrors.findings && (
              <p
                className="create-template-modal__field-error"
                role="alert"
              >
                {fieldErrors.findings}
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="create-template-modal__field">
              <div className="create-template-modal__label">
                Findings{' '}
                <span
                  className="create-template-modal__required-mark"
                  aria-hidden
                >
                  *
                </span>
              </div>
              <div className="create-template-quill-wrap">
                <div
                  className={cn(
                    'create-template-quill',
                    fieldErrors.findings && 'create-template-quill--has-error'
                  )}
                >
                  <ReactQuill
                    theme="snow"
                    value={findings}
                    readOnly={tenantDrawerFieldsDisabled}
                    onChange={content => handleContentChange(content, setFindings, 'Findings')}
                    modules={TEMPLATE_QUILL_MODULES}
                    formats={TEMPLATE_QUILL_FORMATS}
                    placeholder={isViewMode ? '' : TEMPLATE_QUILL_NARRATIVE_PLACEHOLDER}
                  />
                </div>
                <div
                  className={cn(
                    'create-template-modal__count create-template-modal__count--overlay',
                    getWordCount(findings) > TEMPLATE_MAX_CONTENT_WORDS &&
                      'create-template-modal__count--over-limit'
                  )}
                  aria-live="polite"
                >
                  {getWordCount(findings).toLocaleString()} /{' '}
                  {TEMPLATE_MAX_CONTENT_WORDS.toLocaleString()} words
                </div>
              </div>
              {fieldErrors.findings && (
                <p
                  className="create-template-modal__field-error"
                  role="alert"
                >
                  {fieldErrors.findings}
                </p>
              )}
            </div>

            <div className="create-template-modal__field">
              <div className="create-template-modal__label">
                Impressions{' '}
                <span
                  className="create-template-modal__required-mark"
                  aria-hidden
                >
                  *
                </span>
              </div>
              <div className="create-template-quill-wrap">
                <div
                  className={cn(
                    'create-template-quill create-template-quill--impression',
                    fieldErrors.impression && 'create-template-quill--has-error'
                  )}
                >
                  <ReactQuill
                    theme="snow"
                    value={impression}
                    readOnly={tenantDrawerFieldsDisabled}
                    onChange={content => handleContentChange(content, setImpression, 'Impression')}
                    modules={TEMPLATE_QUILL_MODULES}
                    formats={TEMPLATE_QUILL_FORMATS}
                    placeholder={isViewMode ? '' : TEMPLATE_QUILL_NARRATIVE_PLACEHOLDER}
                  />
                </div>
                <div
                  className={cn(
                    'create-template-modal__count create-template-modal__count--overlay',
                    getWordCount(impression) > TEMPLATE_MAX_CONTENT_WORDS &&
                      'create-template-modal__count--over-limit'
                  )}
                  aria-live="polite"
                >
                  {getWordCount(impression).toLocaleString()} /{' '}
                  {TEMPLATE_MAX_CONTENT_WORDS.toLocaleString()} words
                </div>
              </div>
              {fieldErrors.impression && (
                <p
                  className="create-template-modal__field-error"
                  role="alert"
                >
                  {fieldErrors.impression}
                </p>
              )}
            </div>

            <p className="create-template-modal__combined-hint">
              Combined (Findings + Impression): {getCombinedWordCount().toLocaleString()} /{' '}
              {TEMPLATE_MAX_CONTENT_WORDS.toLocaleString()} words max.{' '}
              {TEMPLATE_COMBINED_HINT_BRACKETS}
            </p>
          </>
        )}

        {(isMasterEdit && embedded) || isRadiologistTenantEdit ? (
          <div className="create-template-modal__master-edit-meta create-template-modal__master-edit-meta--version-status-pair">
            <div className="create-template-modal__master-edit-meta-col create-template-modal__master-edit-meta-col--version">
              <label
                className="create-template-modal__label"
                htmlFor={
                  isRadiologistTenantEdit ? 'ris-tenant-edit-version' : 'ris-master-edit-version'
                }
              >
                Version
              </label>
              <input
                id={isRadiologistTenantEdit ? 'ris-tenant-edit-version' : 'ris-master-edit-version'}
                type="text"
                readOnly
                tabIndex={-1}
                value={
                  isRadiologistTenantEdit
                    ? radiologistEmbeddedVersionLabel
                    : masterEditNextVersionLabel
                }
                className="create-template-modal__input create-template-modal__input--version-readonly"
                aria-label={
                  isRadiologistTenantEdit
                    ? `Current version: ${radiologistEmbeddedVersionLabel}`
                    : `Next version after save: ${masterEditNextVersionLabel}`
                }
              />
            </div>
            <div className="create-template-modal__master-edit-meta-col create-template-modal__master-create-status-col">
              <label
                className="create-template-modal__label"
                htmlFor={
                  isRadiologistTenantEdit
                    ? 'ris-tenant-edit-embedded-status-switch'
                    : 'ris-master-edit-embedded-status-switch'
                }
              >
                Status
              </label>
              <div className="create-template-modal__master-create-status-body">
                <p className="create-template-modal__master-edit-status-hint">
                  Active or inactive for tenant catalog.
                </p>
                <ToggleSwitch
                  id={
                    isRadiologistTenantEdit
                      ? 'ris-tenant-edit-embedded-status-switch'
                      : 'ris-master-edit-embedded-status-switch'
                  }
                  checked={!masterEditIsDeleted}
                  onChange={checked => setMasterEditIsDeleted(!checked)}
                  disabled={tenantDrawerFieldsDisabled}
                />
              </div>
            </div>
          </div>
        ) : null}

        {isCatalogNewCreate && !embedded && (
          <div className="create-template-modal__master-edit-meta create-template-modal__master-edit-meta--version-status-pair">
            <div className="create-template-modal__master-edit-meta-col create-template-modal__master-edit-meta-col--version">
              <label
                className="create-template-modal__label"
                htmlFor="ris-master-create-version"
              >
                Version
              </label>
              <input
                id="ris-master-create-version"
                type="text"
                readOnly
                tabIndex={-1}
                value="v1"
                className="create-template-modal__input create-template-modal__input--version-readonly"
                aria-label="Initial version for new template: v1"
              />
            </div>
            <div className="create-template-modal__master-edit-meta-col create-template-modal__master-create-status-col">
              <label
                className="create-template-modal__label"
                htmlFor="ris-master-create-status-switch"
              >
                Status
              </label>
              <div className="create-template-modal__master-create-status-body">
                <p className="create-template-modal__master-edit-status-hint">
                  Active or inactive for tenant catalog.
                </p>
                <ToggleSwitch
                  id="ris-master-create-status-switch"
                  checked={!masterCreateIsDeleted}
                  onChange={checked => setMasterCreateIsDeleted(!checked)}
                  disabled={isViewMode}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="create-template-modal__footer">
        <div className={cn('create-template-modal__footer-actions', !embedded && 'py-1')}>
          {!embedded && (
            <button
              type="button"
              className="create-template-modal__btn-close"
              onClick={handleClose}
            >
              Close
            </button>
          )}
          {tenantSystemTemplateReadOnly ? (
            <button
              type="button"
              className="create-template-modal__btn-primary create-template-modal__btn-duplicate-tenant"
              onClick={handleDuplicateSystemToMyTemplates}
              disabled={duplicateSystemBusy}
            >
              {duplicateSystemBusy ? 'Please wait…' : 'Duplicate to My templates'}
            </button>
          ) : (
            <button
              type="button"
              className="create-template-modal__btn-primary"
              onClick={handlePrimaryAction}
            >
              {primaryLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Templates;
